import React, { useState, useMemo } from "react";
import {
  Row,
  Col,
  Form,
  Input,
  Button,
  DatePicker,
  InputNumber,
  Select,
  Switch,
  Card,
  Divider,
  Space,
  Tag,
  Checkbox,
  Typography,
  Alert,
  Tooltip,
  Badge,
  Slider,
  Popover,
} from "antd";
import {
  SaveOutlined,
  PlusOutlined,
  DeleteOutlined,
  SettingOutlined,
  UserOutlined,
  CalendarOutlined,
  DollarOutlined,
  BgColorsOutlined,
  GlobalOutlined,
  ThunderboltOutlined,
  TrophyOutlined,
  AppstoreOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  EyeOutlined,
  ArrowLeftOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { withRouter } from "react-router";
import { createPromotion } from "@api/subscription/promotion";
import { SuccessNotification, ErrorNotification } from "@components";
import PromotionTemplateGallery from "@views/superAdmin/promotions/components/PromotionTemplateGallery";

const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const promotionTypes = [
  { value: "banner", label: "Banner", icon: "🎯", description: "Top or bottom banner" },
  { value: "popup", label: "Popup", icon: "💬", description: "Modal popup" },
  { value: "floating", label: "Floating", icon: "🎈", description: "Floating widget" },
  { value: "inline", label: "Inline", icon: "📝", description: "Inline content" },
  { value: "exit_intent", label: "Exit Intent", icon: "👋", description: "Exit intent popup" },
  { value: "gamified", label: "Gamified", icon: "🎮", description: "Interactive promotion" },
];

const displayStyles = [
  { value: "minimal", label: "Minimal", description: "Clean and simple" },
  { value: "standard", label: "Standard", description: "Balanced design" },
  { value: "bold", label: "Bold", description: "Eye-catching" },
  { value: "festive", label: "Festive", description: "Celebration themed" },
  { value: "gradient", label: "Gradient", description: "Modern gradient look" },
];

const targetAudiences = [
  { value: "all", label: "All Users", icon: "👥" },
  { value: "free", label: "Free Users", icon: "🆓" },
  { value: "trial", label: "Trial Users", icon: "⏱️" },
  { value: "paid", label: "Paid Users", icon: "💎" },
  { value: "churned", label: "Churned Users", icon: "💔" },
  { value: "new", label: "New Users", icon: "✨" },
  { value: "returning", label: "Returning Users", icon: "🔄" },
];

const themeOptions = [
  { value: "default", label: "Default", color: "#8B5CF6" },
  { value: "dark", label: "Dark", color: "#1f2937" },
  { value: "light", label: "Light", color: "#f3f4f6" },
  { value: "gradient_purple", label: "Purple Gradient", color: "#8B5CF6" },
  { value: "gradient_blue", label: "Blue Gradient", color: "#3B82F6" },
  { value: "gradient_green", label: "Green Gradient", color: "#10B981" },
  { value: "gradient_yellow", label: "Yellow Gradient", color: "#F59E0B" },
  { value: "gradient_orange", label: "Orange Gradient", color: "#F97316" },
  { value: "gradient_pink", label: "Pink Gradient", color: "#EC4899" },
  { value: "gradient_red", label: "Red Gradient", color: "#EF4444" },
  { value: "festive", label: "Festive", color: "#EF4444" },
  { value: "holiday", label: "Holiday", color: "#10B981" },
  { value: "minimal", label: "Minimal", color: "#6B7280" },
  { value: "bold", label: "Bold", color: "#1F2937" },
  { value: "elegant", label: "Elegant", color: "#7C3AED" },
  { value: "playful", label: "Playful", color: "#06B6D4" },
];

const animationOptions = [
  { value: "none", label: "None" },
  { value: "fade", label: "Fade In" },
  { value: "slide", label: "Slide In" },
  { value: "bounce", label: "Bounce" },
  { value: "pulse", label: "Pulse" },
  { value: "shake", label: "Shake" },
];

// Extended gamification types with all options
const gamificationTypes = [
  { value: "none", label: "None", icon: "➖", description: "No gamification" },
  { value: "spin_wheel", label: "Spin Wheel", icon: "🎡", description: "Wheel of fortune style game" },
  { value: "scratch_card", label: "Scratch Card", icon: "🎴", description: "Scratch to reveal prizes" },
  { value: "slot_machine", label: "Slot Machine", icon: "🎰", description: "Slot machine with symbols" },
  { value: "countdown", label: "Countdown Timer", icon: "⏰", description: "Urgency countdown timer" },
  { value: "progress_bar", label: "Progress Bar", icon: "📊", description: "Step-based progress tracker" },
  { value: "streak", label: "Daily Streak", icon: "🔥", description: "Daily streak rewards" },
];

// Preset slot machine symbols
const slotSymbols = [
  { emoji: "🍒", label: "Cherry" },
  { emoji: "🍋", label: "Lemon" },
  { emoji: "🍊", label: "Orange" },
  { emoji: "🍇", label: "Grape" },
  { emoji: "🍉", label: "Watermelon" },
  { emoji: "7️⃣", label: "Seven" },
  { emoji: "💎", label: "Diamond" },
  { emoji: "⭐", label: "Star" },
  { emoji: "🔔", label: "Bell" },
  { emoji: "💰", label: "Money Bag" },
  { emoji: "🎁", label: "Gift" },
  { emoji: "🏆", label: "Trophy" },
  { emoji: "💵", label: "Dollar" },
  { emoji: "🎯", label: "Target" },
  { emoji: "🚀", label: "Rocket" },
];

// ========================================
// GAMIFICATION PRESETS - Default values for each type
// ========================================

const GAMIFICATION_PRESETS = {
  spin_wheel: {
    wheelSegments: [
      { label: "10% Off", value: 10, probability: 25, color: "#8B5CF6" },
      { label: "20% Off", value: 20, probability: 15, color: "#3B82F6" },
      { label: "Free Month", value: 100, probability: 5, color: "#10B981" },
      { label: "5% Off", value: 5, probability: 20, color: "#F59E0B" },
      { label: "Try Again", value: 0, probability: 15, color: "#EF4444" },
      { label: "25% Off", value: 25, probability: 5, color: "#EC4899" },
      { label: "15% Off", value: 15, probability: 10, color: "#6366F1" },
      { label: "Better Luck", value: 0, probability: 5, color: "#14B8A6" },
    ],
    spinDuration: 4000,
    pointerPosition: "top",
  },
  wheel: {
    wheelSegments: [
      { label: "10% Off", value: 10, probability: 25, color: "#8B5CF6" },
      { label: "20% Off", value: 20, probability: 15, color: "#3B82F6" },
      { label: "Free Month", value: 100, probability: 5, color: "#10B981" },
      { label: "5% Off", value: 5, probability: 20, color: "#F59E0B" },
      { label: "Try Again", value: 0, probability: 15, color: "#EF4444" },
      { label: "25% Off", value: 25, probability: 5, color: "#EC4899" },
      { label: "15% Off", value: 15, probability: 10, color: "#6366F1" },
      { label: "Better Luck", value: 0, probability: 5, color: "#14B8A6" },
    ],
    spinDuration: 4000,
    pointerPosition: "top",
  },
  scratch_card: {
    scratchPercentage: 60,
    scratchThreshold: 60,
    revealAnimation: "fade",
    backgroundPattern: "dots",
    scratchColor: "#C0C0C0",
    prizeDisplayText: "Congratulations! You won:",
  },
  scratch: {
    scratchPercentage: 60,
    scratchThreshold: 60,
    revealAnimation: "fade",
    backgroundPattern: "dots",
    scratchColor: "#C0C0C0",
    prizeDisplayText: "Congratulations! You won:",
  },
  slot_machine: {
    slotSymbols: ["🎁", "⭐", "💎", "🎯", "🔥"],
    reels: 3,
    spinDuration: 2000,
    winningSymbol: "💎",
    winningCombinations: [
      { symbols: ["💎", "💎", "💎"], multiplier: 10, label: "Jackpot!" },
      { symbols: ["⭐", "⭐", "⭐"], multiplier: 5, label: "Big Win!" },
      { symbols: ["🎁", "🎁", "🎁"], multiplier: 3, label: "Winner!" },
    ],
  },
  countdown: {
    style: "flip",
    showDays: true,
    showHours: true,
    showMinutes: true,
    showSeconds: true,
    urgencyThreshold: 3600,
    countdownPosition: "top",
    urgencyMessage: "Hurry! Offer ends soon!",
    urgencyColor: "#EF4444",
  },
  progress_bar: {
    style: "gradient",
    milestones: [25, 50, 75, 100],
    animationDuration: 1000,
    showPercentage: true,
    progressSteps: 4,
    currentStep: 1,
    stepLabels: ["Sign Up", "First Action", "Engage", "Complete!"],
    progressColor: "#10B981",
  },
  streak: {
    requiredDays: 7,
    streakDays: 7,
    rewards: [
      { day: 1, label: "Day 1 Starter", value: "5% Off" },
      { day: 3, label: "Day 3 Momentum", value: "10% Off" },
      { day: 5, label: "Day 5 Champion", value: "15% Off" },
      { day: 7, label: "Week Warrior!", value: "25% Off + Free Gift" },
    ],
    resetOnMiss: true,
    streakColor: "#F59E0B",
  },
};

// Vibrant colors for wheel segments
const WHEEL_PRESET_COLORS = [
  "#8B5CF6", // Purple
  "#3B82F6", // Blue
  "#10B981", // Green
  "#F59E0B", // Amber
  "#EF4444", // Red
  "#EC4899", // Pink
  "#6366F1", // Indigo
  "#14B8A6", // Teal
  "#F97316", // Orange
  "#84CC16", // Lime
  "#06B6D4", // Cyan
  "#A855F7", // Violet
];

// Default segment colors
const segmentColors = [
  "#8B5CF6", "#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#EC4899",
  "#6366F1", "#14B8A6", "#F97316", "#84CC16", "#06B6D4", "#A855F7",
];

const showOnPagesOptions = [
  { value: "popup", label: "Popup" },
  { value: "widget", label: "Widget" },
  { value: "settings", label: "Settings" },
  { value: "all", label: "All Pages" },
];

const countdownPositions = [
  { value: "top", label: "Top" },
  { value: "bottom", label: "Bottom" },
  { value: "inline", label: "Inline with content" },
  { value: "floating", label: "Floating" },
];

const initialState = {
  name: "",
  slug: "",
  type: "banner",
  displayStyle: "standard",
  isActive: true,
  priority: 5,
  targetAudience: ["all"],
  excludedUserIds: [],
  includedUserIds: [],
  schedule: {
    startDate: null,
    endDate: null,
  },
  discount: {
    discountType: "percentage",
    discountValue: 0,
    currency: "USD",
    applicablePlans: [],
  },
  coupon: {
    code: "",
    autoApply: false,
  },
  styling: {
    theme: "default",
    primaryColor: "#8B5CF6",
    secondaryColor: "#06B6D4",
    animation: "fade",
  },
  displayConditions: {
    showOnPages: ["all"],
    maxImpressions: 0,
    maxImpressionsPerUser: 3,
    delaySeconds: 0,
    scrollPercentage: 0,
  },
  urgency: {
    showCountdown: false,
    stockLimit: 0,
    usageLimit: 0,
  },
  gamification: {
    type: "none",
    // Spin Wheel / Wheel config
    wheelSegments: [],
    // Scratch Card config
    scratchThreshold: 50,
    prizeDisplayText: "You won!",
    // Slot Machine config
    slotSymbols: ["🍒", "7️⃣", "💎"],
    winningSymbol: "7️⃣",
    spinDuration: 3,
    // Countdown config
    countdownPosition: "top",
    urgencyMessage: "Hurry! Offer ends soon!",
    // Progress Bar config
    progressSteps: 5,
    currentStep: 1,
    stepLabels: [],
    // Streak config
    streakDays: 7,
    streakRewards: [],
  },
  content: {
    en: {
      headline: "",
      subheadline: "",
      ctaText: "",
      ctaSecondaryText: "",
      badge: "",
      terms: "",
    },
  },
};

// Styles
const styles = {
  pageContainer: {
    maxWidth: 1400,
    margin: "0 auto",
    padding: "24px 24px 48px",
  },
  header: {
    marginBottom: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  templateBanner: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    borderRadius: 16,
    padding: "24px 32px",
    marginBottom: 24,
    color: "#fff",
    position: "relative",
    overflow: "hidden",
  },
  templateBannerPattern: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    width: "40%",
    background: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
    opacity: 0.5,
  },
  mainCard: {
    borderRadius: 16,
    border: "1px solid #e5e7eb",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05)",
    overflow: "hidden",
  },
  sectionNav: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    padding: "20px 16px",
    borderRight: "1px solid #f0f0f0",
    background: "#fafafa",
    minHeight: 600,
  },
  navItem: (isActive) => ({
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 16px",
    borderRadius: 10,
    cursor: "pointer",
    transition: "all 0.2s ease",
    background: isActive ? "#fff" : "transparent",
    border: isActive ? "1px solid #e5e7eb" : "1px solid transparent",
    boxShadow: isActive ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
  }),
  navIcon: (isActive, color) => ({
    width: 36,
    height: 36,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: isActive ? `${color}15` : "#f3f4f6",
    color: isActive ? color : "#9ca3af",
    fontSize: 16,
  }),
  sectionContent: {
    padding: "24px 32px",
    minHeight: 600,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 600,
    color: "#111827",
    marginBottom: 8,
  },
  sectionDescription: {
    fontSize: 14,
    color: "#6b7280",
    marginBottom: 24,
  },
  formGroup: {
    marginBottom: 24,
  },
  formLabel: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
    fontWeight: 500,
    color: "#374151",
  },
  inputWrapper: {
    position: "relative",
  },
  colorPickerWrapper: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "8px 12px",
    border: "1px solid #d9d9d9",
    borderRadius: 8,
    background: "#fff",
  },
  colorSwatch: (color) => ({
    width: 32,
    height: 32,
    borderRadius: 6,
    background: color,
    border: "2px solid #fff",
    boxShadow: "0 0 0 1px #d9d9d9",
    cursor: "pointer",
  }),
  previewCard: {
    position: "sticky",
    top: 24,
    borderRadius: 16,
    border: "1px solid #e5e7eb",
    overflow: "hidden",
  },
  previewHeader: {
    padding: "16px 20px",
    background: "#f9fafb",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  previewContent: {
    padding: 20,
    minHeight: 300,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  promotionPreview: (primaryColor, secondaryColor) => ({
    width: "100%",
    padding: 24,
    borderRadius: 12,
    background: `linear-gradient(135deg, ${primaryColor}15, ${secondaryColor}15)`,
    border: `1px solid ${primaryColor}30`,
    textAlign: "center",
  }),
  wheelSegmentRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 16px",
    background: "#f9fafb",
    borderRadius: 10,
    marginBottom: 12,
  },
  streakRewardRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 16px",
    background: "#f9fafb",
    borderRadius: 10,
    marginBottom: 12,
  },
  stepLabelRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "8px 12px",
    background: "#f9fafb",
    borderRadius: 8,
    marginBottom: 8,
  },
  symbolGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: 8,
    padding: 8,
  },
  symbolButton: (isSelected) => ({
    width: 40,
    height: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 20,
    borderRadius: 8,
    border: isSelected ? "2px solid #8B5CF6" : "1px solid #e5e7eb",
    background: isSelected ? "#8B5CF615" : "#fff",
    cursor: "pointer",
    transition: "all 0.2s ease",
  }),
  footer: {
    padding: "20px 32px",
    background: "#f9fafb",
    borderTop: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  probabilityWarning: {
    padding: "12px 16px",
    background: "#FEF3C7",
    borderRadius: 8,
    border: "1px solid #F59E0B",
    marginTop: 16,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  configCard: {
    background: "#f9fafb",
    borderRadius: 12,
    padding: 20,
    marginTop: 16,
    border: "1px solid #e5e7eb",
  },
  configTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 16,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
};

const sections = [
  { key: "basic", label: "Basic Info", icon: <SettingOutlined />, color: "#8B5CF6" },
  { key: "targeting", label: "Targeting", icon: <UserOutlined />, color: "#3B82F6" },
  { key: "schedule", label: "Schedule", icon: <CalendarOutlined />, color: "#10B981" },
  { key: "discount", label: "Discount", icon: <DollarOutlined />, color: "#F59E0B" },
  { key: "styling", label: "Styling", icon: <BgColorsOutlined />, color: "#EC4899" },
  { key: "display", label: "Display Rules", icon: <GlobalOutlined />, color: "#6366F1" },
  { key: "urgency", label: "Urgency", icon: <ThunderboltOutlined />, color: "#EF4444" },
  { key: "gamification", label: "Gamification", icon: <TrophyOutlined />, color: "#F97316" },
  { key: "content", label: "Content", icon: <GlobalOutlined />, color: "#14B8A6" },
];

const PromotionCreateForm = ({ history }) => {
  const [values, setValues] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState("basic");
  const [activeLocale, setActiveLocale] = useState("en");
  const [wheelSegments, setWheelSegments] = useState([
    { label: "10% Off", value: 10, probability: 25, color: "#8B5CF6" },
    { label: "20% Off", value: 20, probability: 15, color: "#3B82F6" },
    { label: "Free Month", value: 100, probability: 5, color: "#10B981" },
    { label: "5% Off", value: 5, probability: 20, color: "#F59E0B" },
    { label: "Try Again", value: 0, probability: 15, color: "#EF4444" },
    { label: "25% Off", value: 25, probability: 5, color: "#EC4899" },
    { label: "15% Off", value: 15, probability: 10, color: "#6366F1" },
    { label: "Better Luck", value: 0, probability: 5, color: "#14B8A6" },
  ]);
  const [streakRewards, setStreakRewards] = useState([
    { day: 1, label: "Day 1 Starter", value: "5% Off" },
    { day: 3, label: "Day 3 Momentum", value: "10% Off" },
    { day: 5, label: "Day 5 Champion", value: "15% Off" },
    { day: 7, label: "Week Warrior!", value: "25% Off + Free Gift" },
  ]);
  const [stepLabels, setStepLabels] = useState(["Sign Up", "First Action", "Engage", "Complete!"]);
  const [templateGalleryVisible, setTemplateGalleryVisible] = useState(false);
  const [selectedTemplateName, setSelectedTemplateName] = useState(null);

  // Calculate wheel probability sum
  const probabilitySum = useMemo(() => {
    return wheelSegments.reduce((sum, seg) => sum + (seg.probability || 0), 0);
  }, [wheelSegments]);

  const handleTemplateSelect = (template) => {
    const { defaults } = template;
    setValues((prev) => ({
      ...prev,
      name: template.name,
      slug: template.id,
      type: defaults.type,
      displayStyle: defaults.displayStyle,
      priority: defaults.priority,
      targetAudience: defaults.targetAudience,
      discount: { ...prev.discount, ...defaults.discount },
      styling: { ...prev.styling, ...defaults.styling },
      displayConditions: { ...prev.displayConditions, ...defaults.displayConditions },
      urgency: { ...prev.urgency, ...defaults.urgency },
      gamification: {
        ...prev.gamification,
        type: defaults.gamification.type,
        // Apply streak-specific fields if present
        ...(defaults.gamification.streakDays && { streakDays: defaults.gamification.streakDays }),
        ...(defaults.gamification.resetOnMiss !== undefined && { resetOnMiss: defaults.gamification.resetOnMiss }),
        ...(defaults.gamification.streakColor && { streakColor: defaults.gamification.streakColor }),
        // Apply scratch card fields if present
        ...(defaults.gamification.scratchThreshold && { scratchThreshold: defaults.gamification.scratchThreshold }),
        ...(defaults.gamification.scratchPercentage && { scratchPercentage: defaults.gamification.scratchPercentage }),
        ...(defaults.gamification.revealAnimation && { revealAnimation: defaults.gamification.revealAnimation }),
        ...(defaults.gamification.backgroundPattern && { backgroundPattern: defaults.gamification.backgroundPattern }),
        ...(defaults.gamification.scratchColor && { scratchColor: defaults.gamification.scratchColor }),
        ...(defaults.gamification.prizeDisplayText && { prizeDisplayText: defaults.gamification.prizeDisplayText }),
        // Apply slot machine fields if present
        ...(defaults.gamification.slotSymbols && { slotSymbols: defaults.gamification.slotSymbols }),
        ...(defaults.gamification.reels && { reels: defaults.gamification.reels }),
        ...(defaults.gamification.spinDuration && { spinDuration: defaults.gamification.spinDuration }),
        ...(defaults.gamification.winningSymbol && { winningSymbol: defaults.gamification.winningSymbol }),
      },
      content: { ...prev.content, en: { ...prev.content.en, ...defaults.content.en } },
    }));
    // Apply wheel segments if present
    if (defaults.gamification.wheelSegments) {
      setWheelSegments(defaults.gamification.wheelSegments);
    }
    // Apply streak rewards if present
    if (defaults.gamification.streakRewards) {
      setStreakRewards(defaults.gamification.streakRewards);
    }
    setSelectedTemplateName(template.name);
    SuccessNotification(`Template "${template.name}" applied successfully!`);
  };

  const handleChange = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleNestedChange = (parent, field, value) => {
    setValues((prev) => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value },
    }));
  };

  const handleContentChange = (locale, field, value) => {
    setValues((prev) => ({
      ...prev,
      content: {
        ...prev.content,
        [locale]: { ...prev.content[locale], [field]: value },
      },
    }));
  };

  const handleGamificationChange = (field, value) => {
    setValues((prev) => ({
      ...prev,
      gamification: { ...prev.gamification, [field]: value },
    }));
  };

  // Apply presets when gamification type changes
  const applyGamificationPreset = (type) => {
    const preset = GAMIFICATION_PRESETS[type];

    if (!preset) {
      // If no preset (e.g., "none"), just update the type
      handleGamificationChange("type", type);
      return;
    }

    // Apply preset values based on type
    const normalizedType = normalizeGamificationType(type);

    if (normalizedType === "spin_wheel" && preset.wheelSegments) {
      setWheelSegments(preset.wheelSegments);
      setValues((prev) => ({
        ...prev,
        gamification: {
          ...prev.gamification,
          type: type,
          spinDuration: preset.spinDuration || 4000,
          pointerPosition: preset.pointerPosition || "top",
        },
      }));
    } else if (normalizedType === "scratch_card") {
      setValues((prev) => ({
        ...prev,
        gamification: {
          ...prev.gamification,
          type: type,
          scratchThreshold: preset.scratchThreshold || 60,
          scratchPercentage: preset.scratchPercentage || 60,
          revealAnimation: preset.revealAnimation || "fade",
          backgroundPattern: preset.backgroundPattern || "dots",
          scratchColor: preset.scratchColor || "#C0C0C0",
          prizeDisplayText: preset.prizeDisplayText || "Congratulations! You won:",
        },
      }));
    } else if (normalizedType === "slot_machine") {
      setValues((prev) => ({
        ...prev,
        gamification: {
          ...prev.gamification,
          type: type,
          slotSymbols: preset.slotSymbols || ["🎁", "⭐", "💎", "🎯", "🔥"],
          reels: preset.reels || 3,
          spinDuration: preset.spinDuration || 2000,
          winningSymbol: preset.winningSymbol || "💎",
          winningCombinations: preset.winningCombinations || [],
        },
      }));
    } else if (normalizedType === "countdown") {
      setValues((prev) => ({
        ...prev,
        gamification: {
          ...prev.gamification,
          type: type,
          countdownStyle: preset.style || "flip",
          showDays: preset.showDays !== false,
          showHours: preset.showHours !== false,
          showMinutes: preset.showMinutes !== false,
          showSeconds: preset.showSeconds !== false,
          urgencyThreshold: preset.urgencyThreshold || 3600,
          countdownPosition: preset.countdownPosition || "top",
          urgencyMessage: preset.urgencyMessage || "Hurry! Offer ends soon!",
          urgencyColor: preset.urgencyColor || "#EF4444",
        },
      }));
    } else if (normalizedType === "progress_bar") {
      const presetLabels = preset.stepLabels || ["Sign Up", "First Action", "Engage", "Complete!"];
      setStepLabels(presetLabels);
      setValues((prev) => ({
        ...prev,
        gamification: {
          ...prev.gamification,
          type: type,
          progressStyle: preset.style || "gradient",
          milestones: preset.milestones || [25, 50, 75, 100],
          animationDuration: preset.animationDuration || 1000,
          showPercentage: preset.showPercentage !== false,
          progressSteps: preset.progressSteps || 4,
          currentStep: preset.currentStep || 1,
          progressColor: preset.progressColor || "#10B981",
        },
      }));
    } else if (normalizedType === "streak") {
      const presetRewards = preset.rewards || [
        { day: 1, label: "Day 1 Starter", value: "5% Off" },
        { day: 3, label: "Day 3 Momentum", value: "10% Off" },
        { day: 5, label: "Day 5 Champion", value: "15% Off" },
        { day: 7, label: "Week Warrior!", value: "25% Off + Free Gift" },
      ];
      setStreakRewards(presetRewards);
      setValues((prev) => ({
        ...prev,
        gamification: {
          ...prev.gamification,
          type: type,
          streakDays: preset.requiredDays || 7,
          resetOnMiss: preset.resetOnMiss !== false,
          streakColor: preset.streakColor || "#F59E0B",
        },
      }));
    } else {
      // Default case - just set the type
      handleGamificationChange("type", type);
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  };

  // Normalize gamification type for comparison
  const normalizeGamificationType = (type) => {
    if (type === "wheel") return "spin_wheel";
    if (type === "scratch") return "scratch_card";
    return type;
  };

  const handleSubmit = async () => {
    // Validation
    if (!values.name) {
      ErrorNotification("Please enter a promotion name");
      return;
    }
    if (!values.schedule.startDate || !values.schedule.endDate) {
      ErrorNotification("Please select start and end dates in the Schedule section");
      setActiveSection("schedule");
      return;
    }
    if (!values.content.en?.headline) {
      ErrorNotification("Please enter a headline in the Content section");
      setActiveSection("content");
      return;
    }

    // Gamification validation
    const normalizedType = normalizeGamificationType(values.gamification?.type);
    if ((normalizedType === "spin_wheel") && probabilitySum !== 100) {
      ErrorNotification("Wheel segment probabilities must sum to 100%");
      setActiveSection("gamification");
      return;
    }

    setLoading(true);
    try {
      // Build gamification config based on type
      const gamificationConfig = {
        enabled: values.gamification?.type !== "none",
        element: normalizeGamificationType(values.gamification?.type) || "none",
        maxPlaysPerUser: 1,
      };

      // Add type-specific config
      if (normalizedType === "spin_wheel") {
        gamificationConfig.wheelSegments = wheelSegments;
      } else if (normalizedType === "scratch_card") {
        gamificationConfig.scratchThreshold = values.gamification?.scratchThreshold || 50;
        gamificationConfig.prizeDisplayText = values.gamification?.prizeDisplayText || "You won!";
      } else if (normalizedType === "slot_machine") {
        gamificationConfig.slotSymbols = values.gamification?.slotSymbols || ["🍒", "7️⃣", "💎"];
        gamificationConfig.winningSymbol = values.gamification?.winningSymbol || "7️⃣";
        gamificationConfig.spinDuration = values.gamification?.spinDuration || 3;
      } else if (normalizedType === "countdown") {
        gamificationConfig.countdownPosition = values.gamification?.countdownPosition || "top";
        gamificationConfig.urgencyMessage = values.gamification?.urgencyMessage || "Hurry! Offer ends soon!";
      } else if (normalizedType === "progress_bar") {
        gamificationConfig.progressSteps = values.gamification?.progressSteps || 5;
        gamificationConfig.currentStep = values.gamification?.currentStep || 1;
        gamificationConfig.stepLabels = stepLabels;
      } else if (normalizedType === "streak") {
        // Server expects streak config nested in gamification.streak
        gamificationConfig.streak = {
          streakDays: values.gamification?.streakDays || 7,
          streakRewards: streakRewards,
          resetOnMiss: values.gamification?.resetOnMiss !== false,
          gracePeriodHours: values.gamification?.gracePeriodHours || 0,
        };
      }

      // Map form values to server schema
      const payload = {
        name: values.name,
        slug: values.slug || generateSlug(values.name),

        // Map form type to server's type enum (seasonal, flash_sale, etc.)
        type: values.type === "banner" ? "seasonal" :
              values.type === "popup" ? "seasonal" :
              values.type === "floating" ? "flash_sale" :
              values.type === "inline" ? "milestone" :
              values.type === "exit_intent" ? "win_back" :
              values.type === "gamified" ? "gamified" : "seasonal",

        // Map form type to displayStyle
        displayStyle: values.type === "banner" ? "banner" :
                      values.type === "popup" ? "modal" :
                      values.type === "floating" ? "floating" :
                      values.type === "inline" ? "inline" :
                      values.type === "exit_intent" ? "modal" :
                      values.type === "gamified" ? "modal" : "banner",

        isActive: values.isActive,
        priority: values.priority,

        // Target audience - server expects string, not array
        targetAudience: Array.isArray(values.targetAudience)
          ? (values.targetAudience.includes("all") ? "all" :
             values.targetAudience.includes("free") ? "free_users" :
             values.targetAudience.includes("trial") ? "trial_users" :
             values.targetAudience.includes("paid") ? "high_value" :
             values.targetAudience.includes("churned") ? "churned" :
             values.targetAudience.includes("new") ? "new_users" :
             values.targetAudience[0] || "all")
          : values.targetAudience || "all",

        // Dates - extract to root level
        startDate: values.schedule.startDate?.toISOString?.()
          || values.schedule.startDate?.format?.()
          || values.schedule.startDate,
        endDate: values.schedule.endDate?.toISOString?.()
          || values.schedule.endDate?.format?.()
          || values.schedule.endDate,

        // Discount - map field names
        discount: {
          type: values.discount.discountType || "percentage",
          value: values.discount.discountValue || 0,
          currency: values.discount.currency || "USD",
        },

        // Coupon
        couponCode: values.coupon.code || undefined,
        couponAutoApply: values.coupon.autoApply || false,

        // Styling
        styling: {
          theme: values.styling.theme || "dark",
          primaryColor: values.styling.primaryColor || "#8B5CF6",
          secondaryColor: values.styling.secondaryColor || "#06B6D4",
          animation: values.styling.animation || "fade",
        },

        // Display conditions
        displayConditions: {
          showOnPages: values.displayConditions.showOnPages || ["all"],
          maxShowsTotal: values.displayConditions.maxImpressions || null,
          maxShowsPerDay: values.displayConditions.maxImpressionsPerUser || null,
          delaySeconds: values.displayConditions.delaySeconds || 0,
          scrollPercentage: values.displayConditions.scrollPercentage || null,
        },

        // Urgency
        urgency: {
          showCountdown: values.urgency.showCountdown || false,
          stockLimit: values.urgency.stockLimit || null,
        },

        // Gamification
        gamification: gamificationConfig,

        // Content - map to server schema structure
        content: {
          default: {
            locale: "en",
            headline: values.content.en?.headline || values.name,
            subheadline: values.content.en?.subheadline || "",
            ctaText: values.content.en?.ctaText || "Get Started",
            badge: values.content.en?.badge || "",
            terms: values.content.en?.terms || "",
          },
          localized: Object.entries(values.content)
            .filter(([locale]) => locale !== "en")
            .map(([locale, content]) => ({
              locale,
              headline: content.headline || "",
              subheadline: content.subheadline || "",
              ctaText: content.ctaText || "Get Started",
              badge: content.badge || "",
              terms: content.terms || "",
            })),
        },
      };

      await createPromotion(payload);
      SuccessNotification("Promotion created successfully");
      history.push("/super-admin/promotions/list");
    } catch (error) {
      console.error("Create promotion error:", error);
      ErrorNotification(error.response?.data?.message || "Error creating promotion");
    } finally {
      setLoading(false);
    }
  };

  // Wheel Segment handlers
  const addWheelSegment = () => {
    const newColor = segmentColors[wheelSegments.length % segmentColors.length];
    setWheelSegments([...wheelSegments, { label: "", value: 0, probability: 0, color: newColor }]);
  };

  const updateWheelSegment = (index, field, value) => {
    const updated = [...wheelSegments];
    updated[index][field] = value;
    setWheelSegments(updated);
  };

  const removeWheelSegment = (index) => {
    setWheelSegments(wheelSegments.filter((_, i) => i !== index));
  };

  // Streak Reward handlers
  const addStreakReward = () => {
    const nextDay = streakRewards.length > 0
      ? Math.max(...streakRewards.map(r => r.day)) + 1
      : 1;
    setStreakRewards([...streakRewards, { day: nextDay, reward: `Day ${nextDay} Bonus`, value: "", type: "credits" }]);
  };

  const updateStreakReward = (index, field, value) => {
    const updated = [...streakRewards];
    updated[index][field] = value;
    setStreakRewards(updated);
  };

  const removeStreakReward = (index) => {
    setStreakRewards(streakRewards.filter((_, i) => i !== index));
  };

  // Step Label handlers
  const updateStepLabels = (steps) => {
    const currentLabels = [...stepLabels];
    while (currentLabels.length < steps) {
      currentLabels.push(`Step ${currentLabels.length + 1}`);
    }
    setStepLabels(currentLabels.slice(0, steps));
  };

  const updateStepLabel = (index, value) => {
    const updated = [...stepLabels];
    updated[index] = value;
    setStepLabels(updated);
  };

  // Symbol picker for slot machine
  const toggleSlotSymbol = (emoji) => {
    const current = values.gamification?.slotSymbols || [];
    if (current.includes(emoji)) {
      if (current.length > 2) {
        handleGamificationChange("slotSymbols", current.filter(s => s !== emoji));
      }
    } else {
      handleGamificationChange("slotSymbols", [...current, emoji]);
    }
  };

  const renderGamificationConfig = () => {
    const gamificationType = normalizeGamificationType(values.gamification?.type);

    switch (gamificationType) {
      case "spin_wheel":
        return (
          <div style={styles.configCard}>
            <div style={styles.configTitle}>
              <span>🎡</span> Prize Wheel Configuration
              <Tag color="purple" style={{ marginLeft: 8 }}>8 Segments Pre-loaded</Tag>
            </div>

            <Alert
              message="How Wheel Probabilities Work"
              description={
                <div>
                  <p style={{ margin: "4px 0" }}>Each segment has a <strong>Win Chance %</strong> that determines how often it lands. All chances must add up to exactly <strong>100%</strong>.</p>
                  <p style={{ margin: "4px 0", color: "#059669" }}>Tip: Lower chances for big prizes, higher chances for small prizes or "Try Again" segments.</p>
                </div>
              }
              type="info"
              showIcon
              style={{ marginBottom: 20, borderRadius: 8 }}
            />

            {/* Column Headers */}
            <div style={{ display: "flex", gap: 12, padding: "0 16px", marginBottom: 8 }}>
              <div style={{ flex: 2 }}>
                <Text type="secondary" style={{ fontSize: 12, fontWeight: 600 }}>SEGMENT LABEL</Text>
              </div>
              <div style={{ flex: 1 }}>
                <Text type="secondary" style={{ fontSize: 12, fontWeight: 600 }}>DISCOUNT VALUE</Text>
              </div>
              <div style={{ flex: 1 }}>
                <Text type="secondary" style={{ fontSize: 12, fontWeight: 600 }}>WIN CHANCE</Text>
              </div>
              <div style={{ width: 70 }}>
                <Text type="secondary" style={{ fontSize: 12, fontWeight: 600 }}>COLOR</Text>
              </div>
              <div style={{ width: 32 }}></div>
            </div>

            {wheelSegments.map((segment, index) => (
              <div key={index} style={{
                ...styles.wheelSegmentRow,
                borderLeft: `4px solid ${segment.color || WHEEL_PRESET_COLORS[index % WHEEL_PRESET_COLORS.length]}`,
              }}>
                <div style={{ flex: 2 }}>
                  <Input
                    placeholder="e.g., 10% Off, Free Month, Try Again"
                    value={segment.label}
                    onChange={(e) => updateWheelSegment(index, "label", e.target.value)}
                    style={{ borderRadius: 6 }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <InputNumber
                    placeholder="0-100"
                    min={0}
                    value={segment.value}
                    onChange={(value) => updateWheelSegment(index, "value", value)}
                    style={{ width: "100%", borderRadius: 6 }}
                    addonAfter="%"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <InputNumber
                    placeholder="1-100"
                    min={0}
                    max={100}
                    value={segment.probability}
                    onChange={(value) => updateWheelSegment(index, "probability", value)}
                    style={{ width: "100%", borderRadius: 6 }}
                    addonAfter="%"
                  />
                </div>
                <div style={{ width: 70, display: "flex", alignItems: "center", gap: 6 }}>
                  <Popover
                    title="Choose Segment Color"
                    content={
                      <div>
                        <div style={{ marginBottom: 8 }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>Preset Colors:</Text>
                        </div>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", maxWidth: 220, marginBottom: 12 }}>
                          {WHEEL_PRESET_COLORS.map((color) => (
                            <div
                              key={color}
                              onClick={() => updateWheelSegment(index, "color", color)}
                              style={{
                                ...styles.colorSwatch(color),
                                width: 28,
                                height: 28,
                                border: segment.color === color ? "3px solid #111" : "2px solid #fff",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
                                transition: "transform 0.15s ease",
                                cursor: "pointer",
                              }}
                              onMouseEnter={(e) => e.target.style.transform = "scale(1.15)"}
                              onMouseLeave={(e) => e.target.style.transform = "scale(1)"}
                            />
                          ))}
                        </div>
                        <Divider style={{ margin: "8px 0" }} />
                        <div style={{ marginBottom: 4 }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>Custom Color:</Text>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <input
                            type="color"
                            value={segment.color || WHEEL_PRESET_COLORS[index % WHEEL_PRESET_COLORS.length]}
                            onChange={(e) => updateWheelSegment(index, "color", e.target.value)}
                            style={{ width: 40, height: 32, border: "none", cursor: "pointer", borderRadius: 4 }}
                          />
                          <Input
                            size="small"
                            value={segment.color || WHEEL_PRESET_COLORS[index % WHEEL_PRESET_COLORS.length]}
                            onChange={(e) => updateWheelSegment(index, "color", e.target.value)}
                            style={{ width: 90, fontFamily: "monospace", fontSize: 12 }}
                            placeholder="#RRGGBB"
                          />
                        </div>
                      </div>
                    }
                    trigger="click"
                    placement="bottomRight"
                  >
                    <div style={{
                      ...styles.colorSwatch(segment.color || WHEEL_PRESET_COLORS[index % WHEEL_PRESET_COLORS.length]),
                      cursor: "pointer",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                    }} />
                  </Popover>
                  <BgColorsOutlined style={{ color: "#9ca3af", fontSize: 12 }} />
                </div>
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeWheelSegment(index)}
                  disabled={wheelSegments.length <= 2}
                  title="Remove segment"
                />
              </div>
            ))}

            <Button
              type="dashed"
              onClick={addWheelSegment}
              icon={<PlusOutlined />}
              style={{ width: "100%", borderRadius: 8, height: 44, marginTop: 8 }}
            >
              Add New Segment
            </Button>

            {/* Probability Status */}
            <div style={{ marginTop: 16 }}>
              {probabilitySum !== 100 ? (
                <div style={{
                  ...styles.probabilityWarning,
                  background: probabilitySum > 100 ? "#FEE2E2" : "#FEF3C7",
                  borderColor: probabilitySum > 100 ? "#EF4444" : "#F59E0B",
                }}>
                  <WarningOutlined style={{ color: probabilitySum > 100 ? "#EF4444" : "#F59E0B" }} />
                  <div>
                    <Text style={{ color: probabilitySum > 100 ? "#DC2626" : "#92400E", fontWeight: 500 }}>
                      {probabilitySum > 100 ? "Over 100%!" : "Not yet 100%"} - Current: <strong>{probabilitySum}%</strong>
                    </Text>
                    <div style={{ fontSize: 12, color: probabilitySum > 100 ? "#DC2626" : "#92400E", marginTop: 2 }}>
                      {probabilitySum > 100
                        ? `Reduce by ${probabilitySum - 100}% to reach exactly 100%`
                        : `Add ${100 - probabilitySum}% more across your segments`}
                    </div>
                  </div>
                </div>
              ) : (
                <Alert
                  message={<span style={{ fontWeight: 500 }}>Perfect! Probabilities total 100%</span>}
                  description="Your wheel is ready to spin. Each segment will land based on its win chance."
                  type="success"
                  showIcon
                  icon={<CheckCircleOutlined />}
                  style={{ borderRadius: 8 }}
                />
              )}
            </div>

            {/* Wheel Settings */}
            <Divider style={{ margin: "20px 0 16px" }}>
              <Text type="secondary" style={{ fontSize: 12 }}>WHEEL SETTINGS</Text>
            </Divider>
            <Row gutter={24}>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Spin Duration
                    <Tooltip title="How long the wheel spins before stopping (in milliseconds)">
                      <InfoCircleOutlined style={{ color: "#9ca3af", marginLeft: 6 }} />
                    </Tooltip>
                  </div>
                  <InputNumber
                    size="large"
                    min={1000}
                    max={10000}
                    step={500}
                    value={values.gamification?.spinDuration || 4000}
                    onChange={(value) => handleGamificationChange("spinDuration", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                    addonAfter="ms"
                  />
                  <Text type="secondary" style={{ fontSize: 11 }}>Recommended: 3000-5000ms for dramatic effect</Text>
                </div>
              </Col>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Pointer Position
                    <Tooltip title="Where the winning indicator arrow is placed">
                      <InfoCircleOutlined style={{ color: "#9ca3af", marginLeft: 6 }} />
                    </Tooltip>
                  </div>
                  <Select
                    size="large"
                    value={values.gamification?.pointerPosition || "top"}
                    onChange={(value) => handleGamificationChange("pointerPosition", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                  >
                    <Option value="top">Top</Option>
                    <Option value="right">Right</Option>
                    <Option value="bottom">Bottom</Option>
                    <Option value="left">Left</Option>
                  </Select>
                </div>
              </Col>
            </Row>
          </div>
        );

      case "scratch_card":
        return (
          <div style={styles.configCard}>
            <div style={styles.configTitle}>
              <span>🎴</span> Scratch Card Configuration
              <Tag color="cyan" style={{ marginLeft: 8 }}>Pre-configured</Tag>
            </div>

            <Alert
              message="Scratch Card Experience"
              description="Users scratch the card surface to reveal their prize. Configure how much they need to scratch and customize the visual appearance."
              type="info"
              showIcon
              style={{ marginBottom: 20, borderRadius: 8 }}
            />

            <Row gutter={24}>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Scratch Percentage Required
                    <Tooltip title="How much of the card surface must be scratched before the prize is revealed">
                      <InfoCircleOutlined style={{ color: "#9ca3af", marginLeft: 6 }} />
                    </Tooltip>
                  </div>
                  <div>
                    <Slider
                      min={10}
                      max={90}
                      value={values.gamification?.scratchThreshold || 60}
                      onChange={(value) => handleGamificationChange("scratchThreshold", value)}
                      marks={{
                        10: "10%",
                        30: "30%",
                        60: "60%",
                        90: "90%",
                      }}
                      trackStyle={{ background: "#3B82F6" }}
                    />
                    <div style={{ textAlign: "center", marginTop: 8 }}>
                      <Tag color="blue" style={{ padding: "4px 12px" }}>
                        User must scratch {values.gamification?.scratchThreshold || 60}% to reveal
                      </Tag>
                    </div>
                    <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 4 }}>
                      Lower = faster reveal, Higher = more engagement
                    </Text>
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Prize Reveal Text
                    <Tooltip title="The message shown above the prize when revealed">
                      <InfoCircleOutlined style={{ color: "#9ca3af", marginLeft: 6 }} />
                    </Tooltip>
                  </div>
                  <Input
                    size="large"
                    placeholder="e.g., Congratulations! You won:"
                    value={values.gamification?.prizeDisplayText || "Congratulations! You won:"}
                    onChange={(e) => handleGamificationChange("prizeDisplayText", e.target.value)}
                    style={{ borderRadius: 8 }}
                  />
                  <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 4 }}>
                    Displayed when the prize is fully revealed
                  </Text>
                </div>
              </Col>
            </Row>

            <Divider style={{ margin: "20px 0 16px" }}>
              <Text type="secondary" style={{ fontSize: 12 }}>VISUAL SETTINGS</Text>
            </Divider>

            <Row gutter={24}>
              <Col span={8}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Scratch Surface Color
                    <Tooltip title="The color of the scratchable layer">
                      <InfoCircleOutlined style={{ color: "#9ca3af", marginLeft: 6 }} />
                    </Tooltip>
                  </div>
                  <div style={styles.colorPickerWrapper}>
                    <input
                      type="color"
                      value={values.gamification?.scratchColor || "#C0C0C0"}
                      onChange={(e) => handleGamificationChange("scratchColor", e.target.value)}
                      style={{ width: 40, height: 40, border: "none", cursor: "pointer", borderRadius: 6 }}
                    />
                    <Input
                      value={values.gamification?.scratchColor || "#C0C0C0"}
                      onChange={(e) => handleGamificationChange("scratchColor", e.target.value)}
                      style={{ border: "none", flex: 1, fontFamily: "monospace" }}
                    />
                  </div>
                </div>
              </Col>
              <Col span={8}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Background Pattern
                    <Tooltip title="Pattern shown on the scratch surface">
                      <InfoCircleOutlined style={{ color: "#9ca3af", marginLeft: 6 }} />
                    </Tooltip>
                  </div>
                  <Select
                    size="large"
                    value={values.gamification?.backgroundPattern || "dots"}
                    onChange={(value) => handleGamificationChange("backgroundPattern", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                  >
                    <Option value="dots">Dots Pattern</Option>
                    <Option value="lines">Lines Pattern</Option>
                    <Option value="solid">Solid Color</Option>
                    <Option value="shimmer">Shimmer Effect</Option>
                  </Select>
                </div>
              </Col>
              <Col span={8}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Reveal Animation
                    <Tooltip title="How the prize appears after scratching">
                      <InfoCircleOutlined style={{ color: "#9ca3af", marginLeft: 6 }} />
                    </Tooltip>
                  </div>
                  <Select
                    size="large"
                    value={values.gamification?.revealAnimation || "fade"}
                    onChange={(value) => handleGamificationChange("revealAnimation", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                  >
                    <Option value="fade">Fade In</Option>
                    <Option value="scale">Scale Up</Option>
                    <Option value="bounce">Bounce</Option>
                    <Option value="confetti">With Confetti</Option>
                  </Select>
                </div>
              </Col>
            </Row>
          </div>
        );

      case "slot_machine":
        return (
          <div style={styles.configCard}>
            <div style={styles.configTitle}>
              <span>🎰</span> Slot Machine Configuration
              <Tag color="orange" style={{ marginLeft: 8 }}>3 Reels Pre-set</Tag>
            </div>

            <Alert
              message="Vegas-Style Slot Machine"
              description="Users pull the lever and watch the reels spin. When they get 3 matching symbols, they win! The preset includes 5 exciting symbols with winning combinations."
              type="info"
              showIcon
              style={{ marginBottom: 20, borderRadius: 8 }}
            />

            <div style={styles.formGroup}>
              <div style={styles.formLabel}>
                Choose Reel Symbols
                <Tooltip title="Select 3-8 symbols that will appear on the slot machine reels. Click to add/remove.">
                  <InfoCircleOutlined style={{ color: "#9ca3af", marginLeft: 6 }} />
                </Tooltip>
              </div>
              <div style={styles.symbolGrid}>
                {slotSymbols.map(({ emoji, label }) => (
                  <Tooltip key={emoji} title={label}>
                    <div
                      style={styles.symbolButton((values.gamification?.slotSymbols || ["🎁", "⭐", "💎", "🎯", "🔥"]).includes(emoji))}
                      onClick={() => toggleSlotSymbol(emoji)}
                    >
                      {emoji}
                    </div>
                  </Tooltip>
                ))}
              </div>
              <div style={{ marginTop: 12, padding: "10px 12px", background: "#f9fafb", borderRadius: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Active on Reels: </Text>
                <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {(values.gamification?.slotSymbols || ["🎁", "⭐", "💎", "🎯", "🔥"]).map((emoji, i) => (
                    <Tag key={i} style={{ fontSize: 18, padding: "6px 10px", background: "#fff", border: "1px solid #e5e7eb" }}>
                      {emoji}
                    </Tag>
                  ))}
                </div>
                <Text type="secondary" style={{ fontSize: 11, marginTop: 6, display: "block" }}>
                  Tip: More symbols = lower win chance. 5-6 symbols is optimal.
                </Text>
              </div>
            </div>

            <Row gutter={24}>
              <Col span={8}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Number of Reels
                    <Tooltip title="Standard slot machines have 3 reels">
                      <InfoCircleOutlined style={{ color: "#9ca3af", marginLeft: 6 }} />
                    </Tooltip>
                  </div>
                  <InputNumber
                    size="large"
                    min={3}
                    max={5}
                    value={values.gamification?.reels || 3}
                    onChange={(value) => handleGamificationChange("reels", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                    addonAfter="reels"
                  />
                </div>
              </Col>
              <Col span={8}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Jackpot Symbol
                    <Tooltip title="3 of this symbol in a row wins the jackpot!">
                      <InfoCircleOutlined style={{ color: "#9ca3af", marginLeft: 6 }} />
                    </Tooltip>
                  </div>
                  <Select
                    size="large"
                    value={values.gamification?.winningSymbol || "💎"}
                    onChange={(value) => handleGamificationChange("winningSymbol", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                  >
                    {(values.gamification?.slotSymbols || ["🎁", "⭐", "💎", "🎯", "🔥"]).map((emoji) => (
                      <Option key={emoji} value={emoji}>
                        <span style={{ fontSize: 18 }}>{emoji}</span>
                        <span style={{ marginLeft: 8 }}>
                          {slotSymbols.find(s => s.emoji === emoji)?.label || emoji}
                        </span>
                      </Option>
                    ))}
                  </Select>
                </div>
              </Col>
              <Col span={8}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Spin Duration
                    <Tooltip title="How long the reels spin before stopping">
                      <InfoCircleOutlined style={{ color: "#9ca3af", marginLeft: 6 }} />
                    </Tooltip>
                  </div>
                  <InputNumber
                    size="large"
                    min={1000}
                    max={5000}
                    step={500}
                    value={values.gamification?.spinDuration || 2000}
                    onChange={(value) => handleGamificationChange("spinDuration", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                    addonAfter="ms"
                  />
                </div>
              </Col>
            </Row>

            {/* Winning Combinations Preview */}
            <Divider style={{ margin: "20px 0 16px" }}>
              <Text type="secondary" style={{ fontSize: 12 }}>WINNING COMBINATIONS</Text>
            </Divider>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              {(values.gamification?.winningCombinations || GAMIFICATION_PRESETS.slot_machine.winningCombinations).map((combo, idx) => (
                <div key={idx} style={{
                  padding: "12px 16px",
                  background: idx === 0 ? "linear-gradient(135deg, #FEF3C7, #FDE68A)" : "#f9fafb",
                  borderRadius: 8,
                  border: idx === 0 ? "2px solid #F59E0B" : "1px solid #e5e7eb",
                  textAlign: "center",
                  minWidth: 120,
                }}>
                  <div style={{ fontSize: 20, marginBottom: 4 }}>
                    {combo.symbols.join(" ")}
                  </div>
                  <Tag color={idx === 0 ? "gold" : idx === 1 ? "blue" : "default"}>
                    {combo.label} ({combo.multiplier}x)
                  </Tag>
                </div>
              ))}
            </div>
          </div>
        );

      case "countdown":
        return (
          <div style={styles.configCard}>
            <div style={styles.configTitle}>
              <span>⏰</span> Countdown Timer Configuration
              <Tag color="red" style={{ marginLeft: 8 }}>Urgency Preset</Tag>
            </div>

            <Alert
              message="Create Urgency with Countdown"
              description="A ticking countdown timer creates FOMO and drives immediate action. The timer counts down to your promotion's end date set in the Schedule section."
              type="info"
              showIcon
              style={{ marginBottom: 20, borderRadius: 8 }}
            />

            <Row gutter={24}>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Timer Display Style
                    <Tooltip title="How the countdown numbers are displayed">
                      <InfoCircleOutlined style={{ color: "#9ca3af", marginLeft: 6 }} />
                    </Tooltip>
                  </div>
                  <Select
                    size="large"
                    value={values.gamification?.countdownStyle || "flip"}
                    onChange={(value) => handleGamificationChange("countdownStyle", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                  >
                    <Option value="flip">Flip Animation (Casino Style)</Option>
                    <Option value="simple">Simple Numbers</Option>
                    <Option value="circular">Circular Progress</Option>
                    <Option value="digital">Digital Clock Style</Option>
                  </Select>
                </div>
              </Col>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Timer Position
                    <Tooltip title="Where the countdown appears in the promotion">
                      <InfoCircleOutlined style={{ color: "#9ca3af", marginLeft: 6 }} />
                    </Tooltip>
                  </div>
                  <Select
                    size="large"
                    value={values.gamification?.countdownPosition || "top"}
                    onChange={(value) => handleGamificationChange("countdownPosition", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                  >
                    {countdownPositions.map((pos) => (
                      <Option key={pos.value} value={pos.value}>{pos.label}</Option>
                    ))}
                  </Select>
                </div>
              </Col>
            </Row>

            <Divider style={{ margin: "20px 0 16px" }}>
              <Text type="secondary" style={{ fontSize: 12 }}>DISPLAY OPTIONS</Text>
            </Divider>

            <Row gutter={16}>
              <Col span={6}>
                <div style={{ textAlign: "center", padding: 12, background: "#f9fafb", borderRadius: 8 }}>
                  <Switch
                    checked={values.gamification?.showDays !== false}
                    onChange={(checked) => handleGamificationChange("showDays", checked)}
                  />
                  <div style={{ marginTop: 8, fontWeight: 500 }}>Days</div>
                </div>
              </Col>
              <Col span={6}>
                <div style={{ textAlign: "center", padding: 12, background: "#f9fafb", borderRadius: 8 }}>
                  <Switch
                    checked={values.gamification?.showHours !== false}
                    onChange={(checked) => handleGamificationChange("showHours", checked)}
                  />
                  <div style={{ marginTop: 8, fontWeight: 500 }}>Hours</div>
                </div>
              </Col>
              <Col span={6}>
                <div style={{ textAlign: "center", padding: 12, background: "#f9fafb", borderRadius: 8 }}>
                  <Switch
                    checked={values.gamification?.showMinutes !== false}
                    onChange={(checked) => handleGamificationChange("showMinutes", checked)}
                  />
                  <div style={{ marginTop: 8, fontWeight: 500 }}>Minutes</div>
                </div>
              </Col>
              <Col span={6}>
                <div style={{ textAlign: "center", padding: 12, background: "#f9fafb", borderRadius: 8 }}>
                  <Switch
                    checked={values.gamification?.showSeconds !== false}
                    onChange={(checked) => handleGamificationChange("showSeconds", checked)}
                  />
                  <div style={{ marginTop: 8, fontWeight: 500 }}>Seconds</div>
                </div>
              </Col>
            </Row>

            <Divider style={{ margin: "20px 0 16px" }}>
              <Text type="secondary" style={{ fontSize: 12 }}>URGENCY SETTINGS</Text>
            </Divider>

            <Row gutter={24}>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Urgency Message
                    <Tooltip title="Text displayed above or beside the countdown">
                      <InfoCircleOutlined style={{ color: "#9ca3af", marginLeft: 6 }} />
                    </Tooltip>
                  </div>
                  <Input
                    size="large"
                    placeholder="e.g., Hurry! Offer ends soon!"
                    value={values.gamification?.urgencyMessage || "Hurry! Offer ends soon!"}
                    onChange={(e) => handleGamificationChange("urgencyMessage", e.target.value)}
                    style={{ borderRadius: 8 }}
                  />
                </div>
              </Col>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Urgency Threshold
                    <Tooltip title="Timer turns red/urgent when this many seconds remain">
                      <InfoCircleOutlined style={{ color: "#9ca3af", marginLeft: 6 }} />
                    </Tooltip>
                  </div>
                  <Select
                    size="large"
                    value={values.gamification?.urgencyThreshold || 3600}
                    onChange={(value) => handleGamificationChange("urgencyThreshold", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                  >
                    <Option value={900}>Last 15 minutes</Option>
                    <Option value={1800}>Last 30 minutes</Option>
                    <Option value={3600}>Last 1 hour</Option>
                    <Option value={7200}>Last 2 hours</Option>
                    <Option value={86400}>Last 24 hours</Option>
                  </Select>
                </div>
              </Col>
            </Row>

            {/* Countdown Target */}
            <div style={{
              marginTop: 16,
              padding: 16,
              background: values.schedule.endDate ? "#ECFDF5" : "#FEF3C7",
              borderRadius: 8,
              border: `1px solid ${values.schedule.endDate ? "#10B981" : "#F59E0B"}`,
            }}>
              {values.schedule.endDate ? (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <CheckCircleOutlined style={{ color: "#10B981" }} />
                    <Text strong>Counting down to:</Text>
                  </div>
                  <div style={{ marginTop: 8, fontSize: 16, fontWeight: 600, color: "#065F46" }}>
                    {values.schedule.endDate.format?.("dddd, MMMM D, YYYY [at] h:mm A") || "Set in Schedule"}
                  </div>
                </>
              ) : (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <WarningOutlined style={{ color: "#F59E0B" }} />
                    <Text strong style={{ color: "#92400E" }}>No end date set</Text>
                  </div>
                  <div style={{ marginTop: 4, color: "#92400E", fontSize: 13 }}>
                    Go to the Schedule section to set your promotion end date for the countdown.
                  </div>
                </>
              )}
            </div>
          </div>
        );

      case "progress_bar":
        return (
          <div style={styles.configCard}>
            <div style={styles.configTitle}>
              <span>📊</span> Progress Bar Configuration
              <Tag color="green" style={{ marginLeft: 8 }}>4 Milestones Pre-set</Tag>
            </div>

            <Alert
              message="Visualize User Progress"
              description="Show users how close they are to unlocking rewards. Great for onboarding flows, achievement tracking, or multi-step discount unlocks."
              type="info"
              showIcon
              style={{ marginBottom: 20, borderRadius: 8 }}
            />

            <Row gutter={24}>
              <Col span={8}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Progress Style
                    <Tooltip title="Visual style of the progress bar">
                      <InfoCircleOutlined style={{ color: "#9ca3af", marginLeft: 6 }} />
                    </Tooltip>
                  </div>
                  <Select
                    size="large"
                    value={values.gamification?.progressStyle || "gradient"}
                    onChange={(value) => handleGamificationChange("progressStyle", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                  >
                    <Option value="gradient">Gradient Fill</Option>
                    <Option value="solid">Solid Color</Option>
                    <Option value="striped">Striped Animation</Option>
                    <Option value="dots">Connected Dots</Option>
                  </Select>
                </div>
              </Col>
              <Col span={8}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Total Milestones
                    <Tooltip title="Number of milestone steps in the progress bar">
                      <InfoCircleOutlined style={{ color: "#9ca3af", marginLeft: 6 }} />
                    </Tooltip>
                  </div>
                  <InputNumber
                    size="large"
                    min={2}
                    max={10}
                    value={values.gamification?.progressSteps || 4}
                    onChange={(value) => {
                      handleGamificationChange("progressSteps", value);
                      updateStepLabels(value);
                    }}
                    style={{ width: "100%", borderRadius: 8 }}
                  />
                </div>
              </Col>
              <Col span={8}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Current Step
                    <Tooltip title="Initial position when user first sees this">
                      <InfoCircleOutlined style={{ color: "#9ca3af", marginLeft: 6 }} />
                    </Tooltip>
                  </div>
                  <InputNumber
                    size="large"
                    min={0}
                    max={values.gamification?.progressSteps || 4}
                    value={values.gamification?.currentStep || 1}
                    onChange={(value) => handleGamificationChange("currentStep", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                  />
                </div>
              </Col>
            </Row>

            <Row gutter={24} style={{ marginTop: 16 }}>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Progress Color
                    <Tooltip title="Color of the filled progress portion">
                      <InfoCircleOutlined style={{ color: "#9ca3af", marginLeft: 6 }} />
                    </Tooltip>
                  </div>
                  <div style={styles.colorPickerWrapper}>
                    <input
                      type="color"
                      value={values.gamification?.progressColor || "#10B981"}
                      onChange={(e) => handleGamificationChange("progressColor", e.target.value)}
                      style={{ width: 40, height: 40, border: "none", cursor: "pointer", borderRadius: 6 }}
                    />
                    <Input
                      value={values.gamification?.progressColor || "#10B981"}
                      onChange={(e) => handleGamificationChange("progressColor", e.target.value)}
                      style={{ border: "none", flex: 1, fontFamily: "monospace" }}
                    />
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>Show Percentage</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
                    <Switch
                      checked={values.gamification?.showPercentage !== false}
                      onChange={(checked) => handleGamificationChange("showPercentage", checked)}
                    />
                    <Text type="secondary">Display % complete to users</Text>
                  </div>
                </div>
              </Col>
            </Row>

            <Divider style={{ margin: "20px 0 16px" }}>
              <Text type="secondary" style={{ fontSize: 12 }}>MILESTONE LABELS</Text>
            </Divider>

            <div style={{ marginBottom: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Name each milestone to guide users through their journey:
              </Text>
            </div>

            {Array.from({ length: values.gamification?.progressSteps || 4 }).map((_, index) => (
              <div key={index} style={{
                ...styles.stepLabelRow,
                borderLeft: `3px solid ${index < (values.gamification?.currentStep || 1) ? (values.gamification?.progressColor || "#10B981") : "#e5e7eb"}`,
              }}>
                <Tag
                  color={index < (values.gamification?.currentStep || 1) ? "success" : "default"}
                  style={{ minWidth: 80, textAlign: "center" }}
                >
                  {index < (values.gamification?.currentStep || 1) ? "Complete" : `Step ${index + 1}`}
                </Tag>
                <Input
                  placeholder={stepLabels[index] || `e.g., ${["Sign Up", "First Action", "Engage", "Complete!"][index] || `Milestone ${index + 1}`}`}
                  value={stepLabels[index] || ""}
                  onChange={(e) => updateStepLabel(index, e.target.value)}
                  style={{ flex: 1, borderRadius: 6 }}
                />
              </div>
            ))}

            {/* Progress Preview */}
            <div style={{ marginTop: 20, padding: 20, background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb" }}>
              <Text type="secondary" style={{ display: "block", marginBottom: 12, fontWeight: 500 }}>Live Preview:</Text>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                {Array.from({ length: values.gamification?.progressSteps || 4 }).map((_, index) => (
                  <div key={index} style={{ flex: 1, display: "flex", alignItems: "center" }}>
                    <div style={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      background: index < (values.gamification?.currentStep || 1) ? (values.gamification?.progressColor || "#10B981") : "#e5e7eb",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontSize: 12,
                      fontWeight: 600,
                      boxShadow: index < (values.gamification?.currentStep || 1) ? "0 2px 4px rgba(16, 185, 129, 0.3)" : "none",
                    }}>
                      {index < (values.gamification?.currentStep || 1) ? "✓" : index + 1}
                    </div>
                    {index < (values.gamification?.progressSteps || 4) - 1 && (
                      <div style={{
                        flex: 1,
                        height: 6,
                        background: index < (values.gamification?.currentStep || 1) - 1 ? (values.gamification?.progressColor || "#10B981") : "#e5e7eb",
                        marginLeft: 4,
                        borderRadius: 3,
                      }} />
                    )}
                  </div>
                ))}
              </div>
              {values.gamification?.showPercentage !== false && (
                <div style={{ textAlign: "center", marginTop: 12 }}>
                  <Tag color="blue" style={{ fontSize: 14, padding: "4px 16px" }}>
                    {Math.round(((values.gamification?.currentStep || 1) / (values.gamification?.progressSteps || 4)) * 100)}% Complete
                  </Tag>
                </div>
              )}
            </div>
          </div>
        );

      case "streak":
        return (
          <div style={styles.configCard}>
            <div style={styles.configTitle}>
              <span>🔥</span> Daily Streak Configuration
              <Tag color="volcano" style={{ marginLeft: 8 }}>7-Day Streak Pre-set</Tag>
            </div>

            <Alert
              message="Build Habits with Streak Rewards"
              description="Encourage users to return daily by offering escalating rewards. Users who miss a day lose their streak and must start over - creating powerful engagement loops."
              type="info"
              showIcon
              style={{ marginBottom: 20, borderRadius: 8 }}
            />

            <Row gutter={24}>
              <Col span={8}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Streak Duration
                    <Tooltip title="How many consecutive days to complete the full streak">
                      <InfoCircleOutlined style={{ color: "#9ca3af", marginLeft: 6 }} />
                    </Tooltip>
                  </div>
                  <InputNumber
                    size="large"
                    min={3}
                    max={30}
                    value={values.gamification?.streakDays || 7}
                    onChange={(value) => handleGamificationChange("streakDays", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                    addonAfter="days"
                  />
                </div>
              </Col>
              <Col span={8}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Streak Color
                    <Tooltip title="Color theme for the streak tracker">
                      <InfoCircleOutlined style={{ color: "#9ca3af", marginLeft: 6 }} />
                    </Tooltip>
                  </div>
                  <div style={styles.colorPickerWrapper}>
                    <input
                      type="color"
                      value={values.gamification?.streakColor || "#F59E0B"}
                      onChange={(e) => handleGamificationChange("streakColor", e.target.value)}
                      style={{ width: 40, height: 40, border: "none", cursor: "pointer", borderRadius: 6 }}
                    />
                    <Input
                      value={values.gamification?.streakColor || "#F59E0B"}
                      onChange={(e) => handleGamificationChange("streakColor", e.target.value)}
                      style={{ border: "none", flex: 1, fontFamily: "monospace" }}
                    />
                  </div>
                </div>
              </Col>
              <Col span={8}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>Reset on Miss</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
                    <Switch
                      checked={values.gamification?.resetOnMiss !== false}
                      onChange={(checked) => handleGamificationChange("resetOnMiss", checked)}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {values.gamification?.resetOnMiss !== false ? "Streak resets if user misses a day" : "Streak continues after missed days"}
                    </Text>
                  </div>
                </div>
              </Col>
            </Row>

            <Divider style={{ margin: "20px 0 16px" }}>
              <Text type="secondary" style={{ fontSize: 12 }}>REWARD MILESTONES</Text>
            </Divider>

            <Alert
              message="Pre-loaded with 4 Milestone Rewards"
              description="Rewards are set for Days 1, 3, 5, and 7. Users see what they can earn at each milestone, motivating them to keep their streak alive!"
              type="success"
              showIcon
              style={{ marginBottom: 16, borderRadius: 8, background: "#ECFDF5", border: "1px solid #10B981" }}
            />

            {/* Column Headers */}
            <div style={{ display: "flex", gap: 12, padding: "0 16px", marginBottom: 8 }}>
              <div style={{ width: 70 }}>
                <Text type="secondary" style={{ fontSize: 11, fontWeight: 600 }}>DAY</Text>
              </div>
              <div style={{ flex: 1 }}>
                <Text type="secondary" style={{ fontSize: 11, fontWeight: 600 }}>MILESTONE NAME</Text>
              </div>
              <div style={{ width: 120 }}>
                <Text type="secondary" style={{ fontSize: 11, fontWeight: 600 }}>TYPE</Text>
              </div>
              <div style={{ width: 100 }}>
                <Text type="secondary" style={{ fontSize: 11, fontWeight: 600 }}>VALUE</Text>
              </div>
              <div style={{ width: 32 }}></div>
            </div>

            {streakRewards.map((reward, index) => (
              <div key={index} style={{
                ...styles.streakRewardRow,
                borderLeft: `3px solid ${values.gamification?.streakColor || "#F59E0B"}`,
              }}>
                <div style={{ width: 70 }}>
                  <InputNumber
                    min={1}
                    max={values.gamification?.streakDays || 7}
                    value={reward.day}
                    onChange={(value) => updateStreakReward(index, "day", value)}
                    style={{ width: "100%", borderRadius: 6 }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <Input
                    placeholder="e.g., Day 3 Momentum"
                    value={reward.reward}
                    onChange={(e) => updateStreakReward(index, "reward", e.target.value)}
                    style={{ borderRadius: 6 }}
                  />
                </div>
                <div style={{ width: 120 }}>
                  <Select
                    value={reward.type || "credits"}
                    onChange={(val) => updateStreakReward(index, "type", val)}
                    style={{ width: "100%" }}
                    size="middle"
                  >
                    <Option value="credits">Credits</Option>
                    <Option value="discount_percentage">% Discount</Option>
                    <Option value="discount_fixed">Fixed Discount</Option>
                    <Option value="free_trial">Free Trial</Option>
                  </Select>
                </div>
                <div style={{ width: 100 }}>
                  <InputNumber
                    placeholder="e.g., 10"
                    value={reward.value}
                    onChange={(val) => updateStreakReward(index, "value", val)}
                    style={{ width: "100%", borderRadius: 6 }}
                    min={0}
                  />
                </div>
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeStreakReward(index)}
                  disabled={streakRewards.length <= 1}
                  title="Remove milestone"
                />
              </div>
            ))}

            <Button
              type="dashed"
              onClick={addStreakReward}
              icon={<PlusOutlined />}
              style={{ width: "100%", borderRadius: 8, height: 44, marginTop: 8 }}
            >
              Add Reward Milestone
            </Button>

            {/* Streak Preview */}
            <div style={{ marginTop: 20, padding: 20, background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb" }}>
              <Text type="secondary" style={{ display: "block", marginBottom: 12, fontWeight: 500 }}>
                Live Preview - {values.gamification?.streakDays || 7} Day Streak:
              </Text>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
                {Array.from({ length: values.gamification?.streakDays || 7 }).map((_, index) => {
                  const dayNum = index + 1;
                  const reward = streakRewards.find(r => r.day === dayNum);
                  const streakColor = values.gamification?.streakColor || "#F59E0B";
                  return (
                    <div key={index} style={{
                      width: 70,
                      padding: 10,
                      borderRadius: 10,
                      background: reward ? `${streakColor}15` : "#f9fafb",
                      border: reward ? `2px solid ${streakColor}` : "1px solid #e5e7eb",
                      textAlign: "center",
                      transition: "all 0.2s ease",
                    }}>
                      <div style={{
                        fontSize: 22,
                        filter: reward ? "none" : "grayscale(100%)",
                        opacity: reward ? 1 : 0.5,
                      }}>
                        {reward ? "🎁" : "📅"}
                      </div>
                      <div style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: reward ? streakColor : "#6b7280",
                      }}>
                        Day {dayNum}
                      </div>
                      {reward && (
                        <div style={{
                          fontSize: 10,
                          color: "#374151",
                          marginTop: 2,
                          fontWeight: 500,
                        }}>
                          {reward.value}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div style={{ textAlign: "center", marginTop: 12 }}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {streakRewards.length} reward milestone{streakRewards.length !== 1 ? "s" : ""} configured
                </Text>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const renderSectionContent = () => {
    switch (activeSection) {
      case "basic":
        return (
          <>
            <div style={styles.sectionTitle}>Basic Information</div>
            <div style={styles.sectionDescription}>
              Set up the fundamental details of your promotion
            </div>

            <Row gutter={24}>
              <Col span={16}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Promotion Name <Text type="danger">*</Text>
                  </div>
                  <Input
                    size="large"
                    placeholder="e.g., Black Friday Sale 2024"
                    value={values.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    onBlur={() => {
                      if (!values.slug) handleChange("slug", generateSlug(values.name));
                    }}
                    style={{ borderRadius: 8 }}
                  />
                </div>
              </Col>
              <Col span={8}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Slug
                    <Tooltip title="URL-friendly identifier">
                      <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                    </Tooltip>
                  </div>
                  <Input
                    size="large"
                    placeholder="black-friday-2024"
                    value={values.slug}
                    onChange={(e) => handleChange("slug", e.target.value)}
                    prefix={<span style={{ color: "#9ca3af" }}>/</span>}
                    style={{ borderRadius: 8 }}
                  />
                </div>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>Promotion Type</div>
                  <Select
                    size="large"
                    value={values.type}
                    onChange={(value) => handleChange("type", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                  >
                    {promotionTypes.map((type) => (
                      <Option key={type.value} value={type.value}>
                        <Space>
                          <span>{type.icon}</span>
                          <span>{type.label}</span>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            - {type.description}
                          </Text>
                        </Space>
                      </Option>
                    ))}
                  </Select>
                </div>
              </Col>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>Display Style</div>
                  <Select
                    size="large"
                    value={values.displayStyle}
                    onChange={(value) => handleChange("displayStyle", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                  >
                    {displayStyles.map((style) => (
                      <Option key={style.value} value={style.value}>
                        <Space>
                          <span>{style.label}</span>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            - {style.description}
                          </Text>
                        </Space>
                      </Option>
                    ))}
                  </Select>
                </div>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Priority
                    <Tooltip title="Higher priority promotions are shown first (1-10)">
                      <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                    </Tooltip>
                  </div>
                  <InputNumber
                    size="large"
                    min={1}
                    max={10}
                    value={values.priority}
                    onChange={(value) => handleChange("priority", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                  />
                </div>
              </Col>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>Status</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
                    <Switch
                      checked={values.isActive}
                      onChange={(checked) => handleChange("isActive", checked)}
                    />
                    <Badge
                      status={values.isActive ? "success" : "default"}
                      text={values.isActive ? "Active" : "Inactive"}
                    />
                  </div>
                </div>
              </Col>
            </Row>
          </>
        );

      case "targeting":
        return (
          <>
            <div style={styles.sectionTitle}>Audience Targeting</div>
            <div style={styles.sectionDescription}>
              Define who should see this promotion
            </div>

            <div style={styles.formGroup}>
              <div style={styles.formLabel}>Target Audience</div>
              <Select
                mode="multiple"
                size="large"
                value={values.targetAudience}
                onChange={(value) => handleChange("targetAudience", value)}
                placeholder="Select target audiences"
                style={{ width: "100%", borderRadius: 8 }}
              >
                {targetAudiences.map((audience) => (
                  <Option key={audience.value} value={audience.value}>
                    <Space>
                      <span>{audience.icon}</span>
                      <span>{audience.label}</span>
                    </Space>
                  </Option>
                ))}
              </Select>
            </div>

            <Divider style={{ margin: "24px 0" }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Advanced Targeting</Text>
            </Divider>

            <Row gutter={24}>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Excluded User IDs
                    <Tooltip title="These users will never see this promotion">
                      <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                    </Tooltip>
                  </div>
                  <Select
                    mode="tags"
                    size="large"
                    value={values.excludedUserIds}
                    onChange={(value) => handleChange("excludedUserIds", value)}
                    placeholder="Enter user IDs to exclude"
                    tokenSeparators={[","]}
                    style={{ width: "100%", borderRadius: 8 }}
                  />
                </div>
              </Col>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Included User IDs
                    <Tooltip title="These users will always see this promotion (overrides other rules)">
                      <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                    </Tooltip>
                  </div>
                  <Select
                    mode="tags"
                    size="large"
                    value={values.includedUserIds}
                    onChange={(value) => handleChange("includedUserIds", value)}
                    placeholder="Enter user IDs to always include"
                    tokenSeparators={[","]}
                    style={{ width: "100%", borderRadius: 8 }}
                  />
                </div>
              </Col>
            </Row>
          </>
        );

      case "schedule":
        return (
          <>
            <div style={styles.sectionTitle}>Schedule</div>
            <div style={styles.sectionDescription}>
              Set when this promotion should be active
            </div>

            <div style={styles.formGroup}>
              <div style={styles.formLabel}>Promotion Period <Text type="danger">*</Text></div>
              <RangePicker
                size="large"
                showTime
                style={{ width: "100%", borderRadius: 8 }}
                value={[values.schedule.startDate, values.schedule.endDate]}
                onChange={(dates) => {
                  setValues((prev) => ({
                    ...prev,
                    schedule: {
                      ...prev.schedule,
                      startDate: dates?.[0] || null,
                      endDate: dates?.[1] || null,
                    },
                  }));
                }}
              />
            </div>

            <Alert
              message="Schedule Required"
              description="Start and end dates are required. The promotion will automatically activate and deactivate based on these dates."
              type="warning"
              showIcon
              style={{ borderRadius: 8 }}
            />
          </>
        );

      case "discount":
        return (
          <>
            <div style={styles.sectionTitle}>
              {values.discount.currency === "credits" ? "Credit Reward Settings" : "Discount Settings"}
            </div>
            <div style={styles.sectionDescription}>
              {values.discount.currency === "credits"
                ? "Configure the credit reward for this promotion"
                : "Configure the discount offer for this promotion"}
            </div>

            <Row gutter={24}>
              {values.discount.currency !== "credits" && (
                <Col span={8}>
                  <div style={styles.formGroup}>
                    <div style={styles.formLabel}>Discount Type</div>
                    <Select
                      size="large"
                      value={values.discount.discountType}
                      onChange={(value) => handleNestedChange("discount", "discountType", value)}
                      style={{ width: "100%", borderRadius: 8 }}
                    >
                      <Option value="percentage">Percentage (%)</Option>
                      <Option value="fixed_amount">Fixed Amount</Option>
                    </Select>
                  </div>
                </Col>
              )}
              <Col span={values.discount.currency === "credits" ? 12 : 8}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    {values.discount.currency === "credits" ? "Credit Amount" : "Discount Value"}
                  </div>
                  <InputNumber
                    size="large"
                    min={0}
                    max={values.discount.discountType === "percentage" && values.discount.currency !== "credits" ? 100 : undefined}
                    value={values.discount.discountValue}
                    onChange={(value) => handleNestedChange("discount", "discountValue", value)}
                    addonAfter={
                      values.discount.currency === "credits"
                        ? "⭐"
                        : values.discount.discountType === "percentage"
                        ? "%"
                        : values.discount.currency
                    }
                    style={{ width: "100%", borderRadius: 8 }}
                  />
                </div>
              </Col>
              <Col span={values.discount.currency === "credits" ? 12 : 8}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    {values.discount.currency === "credits" ? "Reward Type" : "Currency"}
                  </div>
                  <Select
                    size="large"
                    value={values.discount.currency}
                    onChange={(value) => handleNestedChange("discount", "currency", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                  >
                    <Option value="credits">
                      <Space>
                        <span>⭐</span>
                        <span>Credits (Gamification)</span>
                      </Space>
                    </Option>
                    <Option value="USD">USD ($)</Option>
                    <Option value="EUR">EUR (€)</Option>
                    <Option value="GBP">GBP (£)</Option>
                  </Select>
                </div>
              </Col>
            </Row>

            <div style={styles.formGroup}>
              <div style={styles.formLabel}>Applicable Plans</div>
              <Select
                mode="multiple"
                size="large"
                value={values.discount.applicablePlans}
                onChange={(value) => handleNestedChange("discount", "applicablePlans", value)}
                placeholder="Leave empty for all plans"
                style={{ width: "100%", borderRadius: 8 }}
              >
                <Option value="starter">Starter</Option>
                <Option value="pro">Pro</Option>
                <Option value="enterprise">Enterprise</Option>
              </Select>
            </div>

            <Divider style={{ margin: "24px 0" }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Coupon Code</Text>
            </Divider>

            <Row gutter={24}>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>Coupon Code</div>
                  <Input
                    size="large"
                    placeholder="e.g., BLACKFRIDAY50"
                    value={values.coupon.code}
                    onChange={(e) => handleNestedChange("coupon", "code", e.target.value.toUpperCase())}
                    style={{ borderRadius: 8, fontFamily: "monospace", letterSpacing: 1 }}
                  />
                </div>
              </Col>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>Auto Apply</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
                    <Switch
                      checked={values.coupon.autoApply}
                      onChange={(checked) => handleNestedChange("coupon", "autoApply", checked)}
                    />
                    <Text type="secondary">Automatically apply at checkout</Text>
                  </div>
                </div>
              </Col>
            </Row>
          </>
        );

      case "styling":
        return (
          <>
            <div style={styles.sectionTitle}>Visual Styling</div>
            <div style={styles.sectionDescription}>
              Customize the look and feel of your promotion
            </div>

            <Row gutter={24}>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>Theme</div>
                  <Select
                    size="large"
                    value={values.styling.theme}
                    onChange={(value) => handleNestedChange("styling", "theme", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                  >
                    {themeOptions.map((theme) => (
                      <Option key={theme.value} value={theme.value}>
                        <Space>
                          <div style={{
                            width: 16,
                            height: 16,
                            borderRadius: 4,
                            background: theme.color,
                            border: "1px solid #e5e7eb",
                          }} />
                          <span>{theme.label}</span>
                        </Space>
                      </Option>
                    ))}
                  </Select>
                </div>
              </Col>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>Animation</div>
                  <Select
                    size="large"
                    value={values.styling.animation}
                    onChange={(value) => handleNestedChange("styling", "animation", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                  >
                    {animationOptions.map((anim) => (
                      <Option key={anim.value} value={anim.value}>{anim.label}</Option>
                    ))}
                  </Select>
                </div>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>Primary Color</div>
                  <div style={styles.colorPickerWrapper}>
                    <input
                      type="color"
                      value={values.styling.primaryColor}
                      onChange={(e) => handleNestedChange("styling", "primaryColor", e.target.value)}
                      style={{ width: 40, height: 40, border: "none", cursor: "pointer", borderRadius: 6 }}
                    />
                    <Input
                      value={values.styling.primaryColor}
                      onChange={(e) => handleNestedChange("styling", "primaryColor", e.target.value)}
                      style={{ border: "none", flex: 1, fontFamily: "monospace" }}
                    />
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>Secondary Color</div>
                  <div style={styles.colorPickerWrapper}>
                    <input
                      type="color"
                      value={values.styling.secondaryColor}
                      onChange={(e) => handleNestedChange("styling", "secondaryColor", e.target.value)}
                      style={{ width: 40, height: 40, border: "none", cursor: "pointer", borderRadius: 6 }}
                    />
                    <Input
                      value={values.styling.secondaryColor}
                      onChange={(e) => handleNestedChange("styling", "secondaryColor", e.target.value)}
                      style={{ border: "none", flex: 1, fontFamily: "monospace" }}
                    />
                  </div>
                </div>
              </Col>
            </Row>
          </>
        );

      case "display":
        return (
          <>
            <div style={styles.sectionTitle}>Display Rules</div>
            <div style={styles.sectionDescription}>
              Control where and how the promotion appears
            </div>

            <div style={styles.formGroup}>
              <div style={styles.formLabel}>Show On Pages</div>
              <Checkbox.Group
                value={values.displayConditions.showOnPages}
                onChange={(value) => handleNestedChange("displayConditions", "showOnPages", value)}
              >
                <Row gutter={[16, 12]}>
                  {showOnPagesOptions.map((page) => (
                    <Col span={8} key={page.value}>
                      <Checkbox value={page.value} style={{ padding: "8px 0" }}>
                        {page.label}
                      </Checkbox>
                    </Col>
                  ))}
                </Row>
              </Checkbox.Group>
            </div>

            <Divider style={{ margin: "24px 0" }} />

            <Row gutter={24}>
              <Col span={8}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Max Impressions
                    <Tooltip title="Total times this promotion can be shown (0 = unlimited)">
                      <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                    </Tooltip>
                  </div>
                  <InputNumber
                    size="large"
                    min={0}
                    value={values.displayConditions.maxImpressions}
                    onChange={(value) => handleNestedChange("displayConditions", "maxImpressions", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                    placeholder="0 = unlimited"
                  />
                </div>
              </Col>
              <Col span={8}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Max Per Day
                    <Tooltip title="Maximum times per day a user can see this. For daily bonuses (credit rewards), set to 1 - the promotion will reset and show again the next day.">
                      <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                    </Tooltip>
                  </div>
                  <InputNumber
                    size="large"
                    min={0}
                    value={values.displayConditions.maxImpressionsPerUser}
                    onChange={(value) => handleNestedChange("displayConditions", "maxImpressionsPerUser", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                    placeholder="1 = daily"
                  />
                  {values.discount?.currency === "credits" && values.displayConditions.maxImpressionsPerUser === 1 && (
                    <div style={{ marginTop: 4, fontSize: 11, color: "#10B981" }}>
                      ✓ Daily recurring promotion (resets each day)
                    </div>
                  )}
                </div>
              </Col>
              <Col span={8}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Delay (seconds)
                    <Tooltip title="Wait time before showing promotion">
                      <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                    </Tooltip>
                  </div>
                  <InputNumber
                    size="large"
                    min={0}
                    value={values.displayConditions.delaySeconds}
                    onChange={(value) => handleNestedChange("displayConditions", "delaySeconds", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                    addonAfter="sec"
                  />
                </div>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Scroll Trigger
                    <Tooltip title="Show promotion after user scrolls this percentage (0 = immediate)">
                      <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                    </Tooltip>
                  </div>
                  <InputNumber
                    size="large"
                    min={0}
                    max={100}
                    value={values.displayConditions.scrollPercentage}
                    onChange={(value) => handleNestedChange("displayConditions", "scrollPercentage", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                    addonAfter="%"
                  />
                </div>
              </Col>
            </Row>
          </>
        );

      case "urgency":
        return (
          <>
            <div style={styles.sectionTitle}>Urgency Elements</div>
            <div style={styles.sectionDescription}>
              Add urgency to drive faster conversions
            </div>

            <Row gutter={24}>
              <Col span={8}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>Show Countdown</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
                    <Switch
                      checked={values.urgency.showCountdown}
                      onChange={(checked) => handleNestedChange("urgency", "showCountdown", checked)}
                    />
                    <Text type="secondary">Display countdown timer</Text>
                  </div>
                </div>
              </Col>
              <Col span={8}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Stock Limit
                    <Tooltip title="Shows 'Only X left!' message (0 = disabled)">
                      <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                    </Tooltip>
                  </div>
                  <InputNumber
                    size="large"
                    min={0}
                    value={values.urgency.stockLimit}
                    onChange={(value) => handleNestedChange("urgency", "stockLimit", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                  />
                </div>
              </Col>
              <Col span={8}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>
                    Usage Limit
                    <Tooltip title="Max conversions allowed (0 = unlimited)">
                      <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                    </Tooltip>
                  </div>
                  <InputNumber
                    size="large"
                    min={0}
                    value={values.urgency.usageLimit}
                    onChange={(value) => handleNestedChange("urgency", "usageLimit", value)}
                    style={{ width: "100%", borderRadius: 8 }}
                  />
                </div>
              </Col>
            </Row>
          </>
        );

      case "gamification":
        return (
          <>
            <div style={styles.sectionTitle}>Gamification</div>
            <div style={styles.sectionDescription}>
              Add interactive game elements to boost engagement and conversions. Select a type below to get started with optimized presets.
            </div>

            <Alert
              message="Quick Start with Presets"
              description="Each gamification type comes with professionally designed default values. Select a type and we'll auto-populate the best settings - you can customize them afterwards!"
              type="info"
              showIcon
              icon={<ThunderboltOutlined />}
              style={{ marginBottom: 20, borderRadius: 8, background: "#EFF6FF", border: "1px solid #3B82F6" }}
            />

            <div style={styles.formGroup}>
              <div style={styles.formLabel}>
                Choose Your Game Type
                <Tooltip title="Each type is pre-configured with optimal settings. Pick one to see the magic!">
                  <InfoCircleOutlined style={{ color: "#9ca3af", marginLeft: 6 }} />
                </Tooltip>
              </div>
              <Select
                size="large"
                value={values.gamification?.type}
                onChange={(value) => applyGamificationPreset(value)}
                style={{ width: "100%", borderRadius: 8 }}
                optionLabelProp="label"
                placeholder="Select a gamification type to load presets..."
              >
                {gamificationTypes.map((type) => (
                  <Option key={type.value} value={type.value} label={`${type.icon} ${type.label}`}>
                    <Space>
                      <span style={{ fontSize: 18 }}>{type.icon}</span>
                      <div>
                        <div style={{ fontWeight: 500 }}>{type.label}</div>
                        <div style={{ fontSize: 12, color: "#6b7280" }}>{type.description}</div>
                      </div>
                      {GAMIFICATION_PRESETS[type.value] && (
                        <Tag color="green" style={{ marginLeft: 8 }}>Preset Ready</Tag>
                      )}
                    </Space>
                  </Option>
                ))}
              </Select>
              {values.gamification?.type && values.gamification?.type !== "none" && (
                <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
                  <CheckCircleOutlined style={{ color: "#10B981" }} />
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    Default values loaded! Customize below or use as-is.
                  </Text>
                </div>
              )}
            </div>

            {/* Render type-specific configuration */}
            {values.gamification?.type && values.gamification?.type !== "none" && renderGamificationConfig()}
          </>
        );

      case "content":
        return (
          <>
            <div style={styles.sectionTitle}>Promotion Content</div>
            <div style={styles.sectionDescription}>
              Write compelling copy for your promotion
            </div>

            <div style={styles.formGroup}>
              <Space size={8}>
                {["en", "es", "fr"].map((locale) => (
                  <Tag
                    key={locale}
                    color={activeLocale === locale ? "blue" : "default"}
                    onClick={() => {
                      if (!values.content[locale] && locale !== "en") {
                        setValues((prev) => ({
                          ...prev,
                          content: { ...prev.content, [locale]: { ...initialState.content.en } },
                        }));
                      }
                      setActiveLocale(locale);
                    }}
                    style={{ cursor: "pointer", padding: "4px 12px", borderRadius: 6 }}
                  >
                    {locale === "en" ? "English" : locale === "es" ? "Spanish" : "French"}
                  </Tag>
                ))}
              </Space>
            </div>

            <div style={styles.formGroup}>
              <div style={styles.formLabel}>Badge Text</div>
              <Input
                size="large"
                placeholder="e.g., LIMITED TIME"
                value={values.content[activeLocale]?.badge || ""}
                onChange={(e) => handleContentChange(activeLocale, "badge", e.target.value)}
                style={{ borderRadius: 8 }}
              />
            </div>

            <div style={styles.formGroup}>
              <div style={styles.formLabel}>Headline</div>
              <Input
                size="large"
                placeholder="e.g., Black Friday Sale - 50% Off!"
                value={values.content[activeLocale]?.headline || ""}
                onChange={(e) => handleContentChange(activeLocale, "headline", e.target.value)}
                style={{ borderRadius: 8 }}
              />
            </div>

            <div style={styles.formGroup}>
              <div style={styles.formLabel}>Subheadline</div>
              <TextArea
                rows={3}
                placeholder="e.g., Limited time offer. Upgrade your plan today and save big!"
                value={values.content[activeLocale]?.subheadline || ""}
                onChange={(e) => handleContentChange(activeLocale, "subheadline", e.target.value)}
                style={{ borderRadius: 8 }}
              />
            </div>

            <Row gutter={24}>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>Primary CTA</div>
                  <Input
                    size="large"
                    placeholder="e.g., Claim Offer"
                    value={values.content[activeLocale]?.ctaText || ""}
                    onChange={(e) => handleContentChange(activeLocale, "ctaText", e.target.value)}
                    style={{ borderRadius: 8 }}
                  />
                </div>
              </Col>
              <Col span={12}>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>Secondary CTA</div>
                  <Input
                    size="large"
                    placeholder="e.g., Learn More"
                    value={values.content[activeLocale]?.ctaSecondaryText || ""}
                    onChange={(e) => handleContentChange(activeLocale, "ctaSecondaryText", e.target.value)}
                    style={{ borderRadius: 8 }}
                  />
                </div>
              </Col>
            </Row>

            <div style={styles.formGroup}>
              <div style={styles.formLabel}>Terms & Conditions</div>
              <TextArea
                rows={2}
                placeholder="e.g., *Offer valid until Dec 31. Cannot be combined with other offers."
                value={values.content[activeLocale]?.terms || ""}
                onChange={(e) => handleContentChange(activeLocale, "terms", e.target.value)}
                style={{ borderRadius: 8 }}
              />
            </div>
          </>
        );

      default:
        return null;
    }
  };

  // Preview rendering helper
  const renderGamificationPreview = () => {
    const gamificationType = normalizeGamificationType(values.gamification?.type);

    switch (gamificationType) {
      case "spin_wheel":
        if (wheelSegments.length === 0) return null;
        return (
          <div style={{ marginBottom: 16 }}>
            <div style={{
              width: 140,
              height: 140,
              borderRadius: "50%",
              margin: "0 auto 12px",
              position: "relative",
              background: `conic-gradient(${wheelSegments.map((seg, i) => {
                const startAngle = wheelSegments.slice(0, i).reduce((acc, s) => acc + (s.probability || 0), 0) * 3.6;
                const endAngle = startAngle + (seg.probability || 0) * 3.6;
                return `${seg.color || segmentColors[i % segmentColors.length]} ${startAngle}deg ${endAngle}deg`;
              }).join(", ")})`,
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              border: "4px solid #fff",
            }}>
              <div style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "#fff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 16,
              }}>
                🎡
              </div>
            </div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              {wheelSegments.length} segments
            </div>
          </div>
        );

      case "scratch_card":
        return (
          <div style={{
            width: 120,
            height: 80,
            margin: "0 auto 16px",
            background: "linear-gradient(135deg, #9ca3af, #6b7280)",
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontSize: 12,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            flexDirection: "column",
          }}>
            <span style={{ fontSize: 20 }}>🎴</span>
            <span style={{ marginTop: 4 }}>Scratch {values.gamification?.scratchThreshold || 50}%</span>
          </div>
        );

      case "slot_machine":
        return (
          <div style={{
            width: 160,
            height: 60,
            margin: "0 auto 16px",
            background: "linear-gradient(135deg, #f59e0b, #d97706)",
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            border: "3px solid #fbbf24",
          }}>
            {(values.gamification?.slotSymbols || ["🍒", "7️⃣", "💎"]).slice(0, 3).map((emoji, i) => (
              <div key={i} style={{
                width: 36,
                height: 40,
                background: "#fff",
                borderRadius: 4,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 20,
                boxShadow: "inset 0 2px 4px rgba(0,0,0,0.1)",
              }}>
                {emoji}
              </div>
            ))}
          </div>
        );

      case "countdown":
        return (
          <div style={{
            margin: "0 auto 16px",
            padding: "12px 16px",
            background: "linear-gradient(135deg, #EF4444, #DC2626)",
            borderRadius: 8,
            color: "#fff",
            textAlign: "center",
          }}>
            <div style={{ fontSize: 10, marginBottom: 4 }}>{values.gamification?.urgencyMessage || "Hurry!"}</div>
            <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
              {["00", "12", "34", "56"].map((val, i) => (
                <div key={i} style={{
                  background: "rgba(0,0,0,0.2)",
                  padding: "4px 8px",
                  borderRadius: 4,
                  fontSize: 14,
                  fontWeight: 700,
                }}>
                  {val}
                </div>
              ))}
            </div>
          </div>
        );

      case "progress_bar":
        return (
          <div style={{ margin: "0 auto 16px", width: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 4 }}>
              {Array.from({ length: values.gamification?.progressSteps || 5 }).map((_, i) => (
                <div key={i} style={{
                  flex: 1,
                  height: 8,
                  background: i < (values.gamification?.currentStep || 1) ? "#10B981" : "#e5e7eb",
                  borderRadius: i === 0 ? "4px 0 0 4px" : i === (values.gamification?.progressSteps || 5) - 1 ? "0 4px 4px 0" : 0,
                }} />
              ))}
            </div>
            <div style={{ fontSize: 11, color: "#6b7280", textAlign: "center" }}>
              Step {values.gamification?.currentStep || 1} of {values.gamification?.progressSteps || 5}
            </div>
          </div>
        );

      case "streak":
        return (
          <div style={{ margin: "0 auto 16px", textAlign: "center" }}>
            <div style={{ fontSize: 24, marginBottom: 4 }}>🔥</div>
            <div style={{ fontSize: 12, fontWeight: 600 }}>{values.gamification?.streakDays || 7} Day Streak</div>
            <div style={{ display: "flex", justifyContent: "center", gap: 4, marginTop: 8 }}>
              {Array.from({ length: Math.min(values.gamification?.streakDays || 7, 7) }).map((_, i) => (
                <div key={i} style={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  background: i < 3 ? "#F59E0B" : "#e5e7eb",
                }} />
              ))}
            </div>
          </div>
        );

      case "quiz":
        return (
          <div style={{
            width: 160,
            margin: "0 auto 16px",
            padding: 12,
            background: "linear-gradient(135deg, #8b5cf6, #6366f1)",
            borderRadius: 8,
            textAlign: "center",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}>
            <div style={{ fontSize: 24, marginBottom: 4 }}>❓</div>
            <div style={{ color: "#fff", fontSize: 11, fontWeight: 500 }}>
              Answer to win!
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={styles.pageContainer}>
      {/* Header */}
      <div style={styles.header}>
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => history.goBack()}
            style={{ borderRadius: 8 }}
          >
            Back
          </Button>
          <Title level={3} style={{ margin: 0 }}>Create Promotion</Title>
        </Space>
      </div>

      {/* Template Banner */}
      <div style={styles.templateBanner}>
        <div style={styles.templateBannerPattern} />
        <Row align="middle" justify="space-between" style={{ position: "relative", zIndex: 1 }}>
          <Col>
            <Space direction="vertical" size={4}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  background: "rgba(255,255,255,0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                  <AppstoreOutlined style={{ fontSize: 24 }} />
                </div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 600 }}>
                    {selectedTemplateName ? `Using: ${selectedTemplateName}` : "Start from a Template"}
                  </div>
                  <div style={{ fontSize: 14, opacity: 0.9 }}>
                    {selectedTemplateName
                      ? "Template values applied to form"
                      : "Choose from 25+ pre-built promotion templates"}
                  </div>
                </div>
              </div>
            </Space>
          </Col>
          <Col>
            <Button
              size="large"
              onClick={() => setTemplateGalleryVisible(true)}
              style={{
                borderRadius: 10,
                background: "rgba(255,255,255,0.2)",
                border: "1px solid rgba(255,255,255,0.3)",
                color: "#fff",
                height: 44,
                padding: "0 24px",
              }}
            >
              {selectedTemplateName ? "Change Template" : "Browse Templates"}
            </Button>
          </Col>
        </Row>
      </div>

      {/* Main Content */}
      <Row gutter={24}>
        <Col span={17}>
          <Card style={styles.mainCard} bodyStyle={{ padding: 0 }}>
            <Row>
              {/* Section Navigation */}
              <Col flex="220px">
                <div style={styles.sectionNav}>
                  {sections.map((section) => (
                    <div
                      key={section.key}
                      style={styles.navItem(activeSection === section.key)}
                      onClick={() => setActiveSection(section.key)}
                    >
                      <div style={styles.navIcon(activeSection === section.key, section.color)}>
                        {section.icon}
                      </div>
                      <div>
                        <div style={{
                          fontWeight: activeSection === section.key ? 600 : 400,
                          color: activeSection === section.key ? "#111827" : "#6b7280",
                          fontSize: 14,
                        }}>
                          {section.label}
                        </div>
                      </div>
                      {activeSection === section.key && (
                        <CheckCircleOutlined style={{ marginLeft: "auto", color: section.color }} />
                      )}
                    </div>
                  ))}
                </div>
              </Col>

              {/* Section Content */}
              <Col flex="1">
                <div style={styles.sectionContent}>
                  <Form layout="vertical">
                    {renderSectionContent()}
                  </Form>
                </div>
              </Col>
            </Row>

            {/* Footer */}
            <div style={styles.footer}>
              <Text type="secondary">
                {values.name ? `Creating: ${values.name}` : "Fill in the details to create your promotion"}
              </Text>
              <Space>
                <Button size="large" onClick={() => history.goBack()} style={{ borderRadius: 8 }}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  size="large"
                  loading={loading}
                  onClick={handleSubmit}
                  style={{ borderRadius: 8, height: 44, padding: "0 32px" }}
                >
                  <Space size={8}>
                    <SaveOutlined />
                    <span>Create Promotion</span>
                  </Space>
                </Button>
              </Space>
            </div>
          </Card>
        </Col>

        {/* Preview Panel */}
        <Col span={7}>
          <Card style={styles.previewCard} bodyStyle={{ padding: 0 }}>
            <div style={styles.previewHeader}>
              <Space>
                <EyeOutlined />
                <Text strong>Live Preview</Text>
              </Space>
              <Tag color={values.isActive ? "success" : "default"}>
                {values.isActive ? "Active" : "Inactive"}
              </Tag>
            </div>
            <div style={styles.previewContent}>
              <div style={styles.promotionPreview(values.styling.primaryColor, values.styling.secondaryColor)}>
                {values.content.en.badge && (
                  <Tag
                    style={{
                      background: values.styling.primaryColor,
                      color: "#fff",
                      border: "none",
                      marginBottom: 12,
                      padding: "4px 12px",
                      borderRadius: 4,
                    }}
                  >
                    {values.content.en.badge}
                  </Tag>
                )}
                <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: "#111827" }}>
                  {values.content.en.headline || "Your Headline Here"}
                </div>
                <div style={{ fontSize: 14, color: "#6b7280", marginBottom: 16 }}>
                  {values.content.en.subheadline || "Your subheadline will appear here"}
                </div>

                {/* Gamification Preview */}
                {renderGamificationPreview()}

                {values.discount.discountValue > 0 && (
                  <div style={{
                    fontSize: 28,
                    fontWeight: 700,
                    color: values.styling.primaryColor,
                    marginBottom: 16,
                  }}>
                    {values.discount.currency === "credits" ? (
                      <>+{values.discount.discountValue} Credits</>
                    ) : values.discount.discountType === "percentage" ? (
                      <>{values.discount.discountValue}% OFF</>
                    ) : (
                      <>{values.discount.currency === "USD" ? "$" : values.discount.currency === "EUR" ? "€" : values.discount.currency === "GBP" ? "£" : ""}{values.discount.discountValue} OFF</>
                    )}
                  </div>
                )}
                <Space>
                  <Button
                    type="primary"
                    style={{
                      background: values.styling.primaryColor,
                      borderColor: values.styling.primaryColor,
                      borderRadius: 6,
                    }}
                  >
                    {values.content.en.ctaText || "CTA Button"}
                  </Button>
                  {values.content.en.ctaSecondaryText && (
                    <Button style={{ borderRadius: 6 }}>
                      {values.content.en.ctaSecondaryText}
                    </Button>
                  )}
                </Space>
                {values.content.en.terms && (
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 16 }}>
                    {values.content.en.terms}
                  </div>
                )}
              </div>
            </div>

            {/* Preview Info */}
            <div style={{ padding: 16, borderTop: "1px solid #e5e7eb" }}>
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Text type="secondary">Type:</Text>
                  <Text>{promotionTypes.find(t => t.value === values.type)?.label}</Text>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Text type="secondary">Style:</Text>
                  <Text>{displayStyles.find(s => s.value === values.displayStyle)?.label}</Text>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Text type="secondary">Priority:</Text>
                  <Text>{values.priority}</Text>
                </div>
                {values.discount.discountValue > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <Text type="secondary">
                      {values.discount.currency === "credits" ? "Reward:" : "Discount:"}
                    </Text>
                    <Tag color={values.discount.currency === "credits" ? "gold" : "green"}>
                      {values.discount.currency === "credits"
                        ? `+${values.discount.discountValue} ⭐`
                        : values.discount.discountType === "percentage"
                        ? `${values.discount.discountValue}%`
                        : `${values.discount.currency} ${values.discount.discountValue}`}
                    </Tag>
                  </div>
                )}
                {values.gamification?.type !== "none" && (
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <Text type="secondary">Gamification:</Text>
                    <Tag color="purple">{gamificationTypes.find(g => g.value === values.gamification?.type)?.label}</Tag>
                  </div>
                )}
              </Space>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Template Gallery Modal */}
      <PromotionTemplateGallery
        visible={templateGalleryVisible}
        onClose={() => setTemplateGalleryVisible(false)}
        onSelect={handleTemplateSelect}
      />
    </div>
  );
};

export default withRouter(PromotionCreateForm);
